import { useState, useEffect, useRef } from "react";
import { createClient } from "@supabase/supabase-js";

const fontLink = document.createElement("link");
fontLink.rel = "stylesheet";
fontLink.href = "https://fonts.googleapis.com/css2?family=Bebas+Neue&family=DM+Sans:wght@400;500;600;700;800&family=DM+Mono:wght@400;500;600&display=swap";
document.head.appendChild(fontLink);

const SUPABASE_URL  = "https://jsyzxscinfrfcebiqmja.supabase.co";
const SUPABASE_KEY  = "sb_publishable_KtY55FFvhf5WfSZpO3nSoA_02XnFl1l";
const GOOGLE_SV_KEY = "AIzaSyBqyGSYeZCMa7zoYJOtnKLgzPz8Ei0o9DM";
const ANTHROPIC_KEY  = process.env.REACT_APP_ANTHROPIC_KEY || "";
const RENTCAST_KEY   = process.env.REACT_APP_RENTCAST_KEY || "";
const BACKEND_URL    = process.env.REACT_APP_BACKEND_URL || "https://wholesaleiq-production.up.railway.app";

const TIER_LIMITS = {
  starter: { leads:250, markets:1,  rentcast:20,  sms:0,    members:1  },
  pro:     { leads:-1,  markets:3,  rentcast:75,  sms:500,  members:3  },
  team:    { leads:-1,  markets:-1, rentcast:300, sms:2000, members:10 },
};

const TIER_PRICES = {
  starter: { label:"Starter", price:"$74.99", priceId:"price_1TFqqxFY6htG3tXMeEwBcgJ7" },
  pro:     { label:"Pro",     price:"$119.99", priceId:"price_1TFqrKFY6htG3tXM1uf6RkRR" },
  team:    { label:"Team",    price:"$199.99", priceId:"price_1TFqrhFY6htG3tXMrBTlCiKo" },
};

const supabase = createClient(SUPABASE_URL, SUPABASE_KEY);

// ─── DB helpers ───────────────────────────────────────────────────────────────
function toRow(lead, userId, orgId) {
  return {
    id:               lead.id,
    user_id:          userId,
    org_id:           orgId || null,
    stage:            lead.stage,
    name:             lead.name,
    phone:            lead.phone || "",
    address:          lead.address || "",
    sqft:             lead.sqft || null,
    equity:           lead.equity || 0,
    tax_lien:         lead.taxLien || false,
    vacant:           lead.vacant || false,
    score:            lead.score || 50,
    hot:              lead.hot || false,
    notes:            lead.notes || "",
    arv:              lead.arv || null,
    comps:            lead.comps || [],
    rehab_tier:       lead.rehabTier || null,
    condition_report: lead.conditionReport || null,
    sms_history:      lead.smsHistory || [],
    follow_ups:       lead.followUps || [],
    last_contact:     lead.lastContact || null,
    market:           lead.market || "",
    property_type:    lead.propertyType || "Single Family",
    bedrooms:         lead.bedrooms || null,
    bathrooms:        lead.bathrooms || null,
    wholesale_fee:    lead.wholesaleFee || null,
    photos:           lead.photos || [],
  };
}

function fromRow(row) {
  return {
    id:              row.id,
    stage:           row.stage,
    name:            row.name,
    phone:           row.phone,
    address:         row.address,
    sqft:            row.sqft,
    equity:          row.equity,
    taxLien:         row.tax_lien,
    vacant:          row.vacant,
    score:           row.score,
    hot:             row.hot,
    notes:           row.notes,
    arv:             row.arv,
    comps:           row.comps || [],
    rehabTier:       row.rehab_tier,
    conditionReport: row.condition_report,
    smsHistory:      row.sms_history || [],
    followUps:       row.follow_ups || [],
    lastContact:     row.last_contact,
    market:          row.market || "",
    propertyType:    row.property_type || "Single Family",
    bedrooms:        row.bedrooms || null,
    bathrooms:       row.bathrooms || null,
    wholesaleFee:    row.wholesale_fee || null,
    photos:          row.photos || [],
  };
}

// ─── Constants ────────────────────────────────────────────────────────────────
const STAGES = [
  { id:"new",       label:"New Lead",              color:"#C9A96E", icon:"◎" },
  { id:"attempted", label:"Attempted Contact",     color:"#C9935A", icon:"◌" },
  { id:"talking",   label:"Connected / Talking",   color:"#5A8A60", icon:"◉" },
  { id:"offer",     label:"Offer Made",            color:"#9B7ED4", icon:"◈" },
  { id:"contract",  label:"Under Contract",        color:"#B87AA8", icon:"★" },
  { id:"dead",      label:"Dead / Not Interested", color:"#45454A", icon:"✕" },
];

const DEFAULT_REHAB = {
  light:  { label:"Light Rehab",  color:"#5A8A60", items:[{name:"Paint (interior)",costPerSqft:1.50},{name:"Flooring",costPerSqft:3.00},{name:"Fixtures / hardware",costPerSqft:0.75},{name:"Deep clean & haul",costPerSqft:0.50}]},
  medium: { label:"Medium Rehab", color:"#C9935A", items:[{name:"Paint (int + ext)",costPerSqft:2.25},{name:"Flooring",costPerSqft:4.50},{name:"Kitchen update",costPerSqft:3.00},{name:"Bath update",costPerSqft:2.50},{name:"HVAC service",costPerSqft:1.25},{name:"Landscaping",costPerSqft:0.75}]},
  heavy:  { label:"Heavy Rehab",  color:"#C06149", items:[{name:"Full gut / demo",costPerSqft:3.00},{name:"Structural repairs",costPerSqft:4.00},{name:"Roof replacement",costPerSqft:2.50},{name:"Full kitchen remodel",costPerSqft:5.00},{name:"Full bath remodel",costPerSqft:3.50},{name:"HVAC replacement",costPerSqft:2.00},{name:"Electrical/plumbing",costPerSqft:3.00},{name:"Windows/doors",costPerSqft:2.00},{name:"Landscaping full",costPerSqft:1.00}]},
};

const BL_MAP = {
  name:    ["Owner Name","owner_name","First Name","full_name","Name"],
  phone:   ["Phone","phone","Mobile Phone","Cell Phone","phone_number"],
  address: ["Property Address","address","Street Address","property_address"],
  city:    ["City","city","Mailing City"],
  state:   ["State","state","Mailing State"],
  zip:     ["Zip","zip","Zip Code","Mailing Zip"],
  sqft:    ["Sqft","sqft","Square Feet","Living Sqft","building_sqft"],
  equity:  ["Equity %","equity_percent","Equity Percent","Estimated Equity %"],
  taxLien: ["Tax Lien","tax_lien","Tax Delinquent","tax_delinquent"],
  vacant:  ["Vacant","vacant","Vacancy","Occupancy Status"],
};

function findCol(headers, keys) {
  for (const k of keys) {
    const f = headers.find(h=>h.trim().toLowerCase()===k.toLowerCase());
    if (f) return f;
  }
  return null;
}

function parseCSV(text) {
  const lines = text.trim().split("\n");
  if (lines.length < 2) return [];
  const headers = lines[0].split(",").map(h=>h.replace(/"/g,"").trim());
  const cols = {};
  for (const [field,keys] of Object.entries(BL_MAP)) cols[field]=findCol(headers,keys);
  const leads = [];
  for (let i=1;i<lines.length;i++) {
    const vals = lines[i].split(",").map(v=>v.replace(/"/g,"").trim());
    const row = {};
    headers.forEach((h,idx)=>{row[h]=vals[idx]||"";});
    const addrParts = [cols.address?row[cols.address]:"",cols.city?row[cols.city]:"",cols.state?row[cols.state]:"",cols.zip?row[cols.zip]:""].filter(Boolean).join(", ");
    const name = cols.name?row[cols.name]:"";
    if (!name) continue;
    const taxRaw=cols.taxLien?row[cols.taxLien]:"";
    const vacRaw=cols.vacant?row[cols.vacant]:"";
    leads.push({ id:Date.now()+i, stage:"new", name, hot:false, phone:cols.phone?row[cols.phone]:"", address:addrParts||"Unknown", sqft:cols.sqft?parseInt(row[cols.sqft])||null:null, equity:cols.equity?parseInt(row[cols.equity])||0:0, taxLien:/yes|true|1|y/i.test(taxRaw), vacant:/yes|true|1|y|vacant/i.test(vacRaw), score:50, notes:"", arv:null, comps:[], rehabTier:null, conditionReport:null, smsHistory:[], followUps:[], lastContact:null });
  }
  return leads;
}

const fmt$ = n => "$"+Math.round(n).toLocaleString();
function calcMAO(lead,rehabCost,maoFormula) {
  if(!lead.arv) return 0;
  const arv=lead.arv.value;
  const pt=lead.propertyType||"Single Family";
  if(pt==="Land"){
    const f=(maoFormula&&maoFormula.land)||{multiplier:50,assignmentFee:0};
    return Math.round(arv*(f.multiplier||50)/100-(f.assignmentFee||0));
  }
  const key=pt==="Multi-Family"?"multiFamily":pt==="Commercial"?"commercial":"singleFamily";
  const f=(maoFormula&&maoFormula[key])||{multiplier:70,closingCosts:0,minProfit:0,assignmentFee:0};
  return Math.round(arv*(f.multiplier||70)/100-(lead.rehabTier?rehabCost:0)-arv*(f.closingCosts||0)/100-(f.minProfit||0)-(f.assignmentFee||0));
}
const stageOf = id => STAGES.find(s=>s.id===id);
function calcRehab(tier,sqft,config,market="") {
  if(!tier)return 0;
  const mktCfg = (market && config[market] && config[market][tier]) ? config[market][tier]
               : (config.__default__ && config.__default__[tier]) ? config.__default__[tier]
               : config[tier]; // legacy flat config fallback
  if(!mktCfg)return 0;
  return mktCfg.items.reduce((sum,item)=>sum+item.costPerSqft*(sqft||1400),0);
}

const Tag = ({label,color="#C9A96E",dim})=>(
  <span style={{fontSize:10,fontFamily:"'DM Mono',monospace",fontWeight:600,letterSpacing:.5,background:dim?"#1C1C1F":color+"18",border:`1px solid ${dim?"#2A2A2E":color+"44"}`,color:dim?"#45454A":color,borderRadius:4,padding:"2px 7px"}}>{label}</span>
);

const Bubble = ({msg})=>(
  <div style={{display:"flex",justifyContent:msg.from==="in"?"flex-start":"flex-end",marginBottom:8}}>
    <div style={{maxWidth:"82%",padding:"9px 13px",borderRadius:msg.from==="in"?"4px 14px 14px 14px":"14px 4px 14px 14px",background:msg.from==="in"?"#1C1C1F":"#1A1A1E",border:`1px solid ${msg.from==="in"?"#2A2A2E":"#C9A96E33"}`,fontSize:12.5,color:"#C4C2BD",lineHeight:1.55}}>
      {msg.text}
      <div style={{fontSize:10,color:"#45454A",marginTop:4,textAlign:msg.from==="in"?"left":"right"}}>{msg.time}</div>
    </div>
  </div>
);

// ─── Saving indicator ─────────────────────────────────────────────────────────
const SavingDot = ({saving}) => (
  <div style={{display:"flex",alignItems:"center",gap:6,fontSize:11,color:saving?"#C9935A":"#5A8A60",fontFamily:"'DM Mono',monospace"}}>
    <div style={{width:6,height:6,borderRadius:"50%",background:saving?"#C9935A":"#5A8A60",animation:saving?"pulse 1s infinite":"none"}}/>
    {saving?"SAVING...":"SAVED"}
    <style>{`@keyframes pulse{0%,100%{opacity:1}50%{opacity:.3}}`}</style>
  </div>
);

// ─── CSV Import Modal ─────────────────────────────────────────────────────────
function CSVImportModal({onClose,onImport}) {
  const [isDragging,setIsDragging]=useState(false);
  const [preview,setPreview]=useState(null);
  const [parsed,setParsed]=useState([]);
  const [error,setError]=useState("");
  const fileRef=useRef();
  const handleFile=(file)=>{
    if(!file||!file.name.endsWith(".csv")){setError("Please upload a .csv file");return;}
    const reader=new FileReader();
    reader.onload=(e)=>{const leads=parseCSV(e.target.result);if(leads.length===0){setError("No valid leads found.");return;}setParsed(leads);setPreview(leads.slice(0,5));setError("");};
    reader.readAsText(file);
  };
  const onDrop=(e)=>{e.preventDefault();setIsDragging(false);handleFile(e.dataTransfer.files[0]);};
  return (
    <div style={{position:"fixed",inset:0,background:"#000C",zIndex:200,display:"flex",alignItems:"center",justifyContent:"center"}} onClick={e=>e.target===e.currentTarget&&onClose()}>
      <div style={{background:"#111113",border:"1px solid #1C1C1F",borderRadius:14,width:700,maxHeight:"88vh",overflow:"hidden",display:"flex",flexDirection:"column",boxShadow:"0 30px 80px #0009"}}>
        <div style={{padding:"22px 28px 16px",borderBottom:"1px solid #1C1C1F",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
          <div><div style={{fontSize:10,letterSpacing:3,color:"#C9A96E",fontFamily:"'DM Mono',monospace",marginBottom:4}}>BATCH LEADS</div><div style={{fontSize:20,fontWeight:800,color:"#E8E6E1"}}>Import CSV</div></div>
          <button onClick={onClose} style={{background:"none",border:"none",color:"#6B6B70",cursor:"pointer",fontSize:22}}>✕</button>
        </div>
        <div style={{overflowY:"auto",padding:24,flex:1}}>
          <div onDragOver={e=>{e.preventDefault();setIsDragging(true);}} onDragLeave={()=>setIsDragging(false)} onDrop={onDrop} onClick={()=>fileRef.current.click()}
            style={{border:`2px dashed ${isDragging?"#C9A96E":"#2A2A2E"}`,borderRadius:12,padding:"40px 24px",textAlign:"center",cursor:"pointer",marginBottom:20,background:isDragging?"#C9A96E08":"transparent",transition:"all .2s"}}>
            <div style={{fontSize:36,marginBottom:10}}>📂</div>
            <div style={{fontSize:14,fontWeight:700,color:"#C4C2BD",marginBottom:6}}>Drop your BatchLeads CSV here</div>
            <div style={{fontSize:12,color:"#45454A",marginBottom:14}}>or click to browse</div>
            <div style={{display:"flex",gap:8,justifyContent:"center",flexWrap:"wrap"}}>
              {["Owner Name","Phone","Property Address","Equity %","Tax Lien","Vacant","Sqft"].map(f=><Tag key={f} label={f} color="#C9A96E" dim/>)}
            </div>
            <input ref={fileRef} type="file" accept=".csv" style={{display:"none"}} onChange={e=>handleFile(e.target.files[0])}/>
          </div>
          {error&&<div style={{background:"#C0614911",border:"1px solid #C0614933",borderRadius:8,padding:"10px 14px",marginBottom:16,color:"#C06149",fontSize:13}}>⚠ {error}</div>}
          {!preview&&(
            <div style={{background:"#111113",border:"1px solid #1C1C1F",borderRadius:10,padding:18}}>
              <div style={{fontSize:11,letterSpacing:2,color:"#45454A",fontFamily:"'DM Mono',monospace",marginBottom:12}}>HOW TO EXPORT FROM BATCHLEADS</div>
              {["1. Log into BatchLeads and open your list","2. Apply distress filters (equity, tax lien, vacant)","3. Click Export → Download as CSV","4. Drag the downloaded file into the box above"].map((s,i)=>(
                <div key={i} style={{fontSize:13,color:"#8A8885",marginBottom:8,display:"flex",gap:10}}><span style={{color:"#C9A96E"}}>→</span>{s}</div>
              ))}
            </div>
          )}
          {preview&&(
            <div>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12}}>
                <div style={{fontSize:13,fontWeight:700,color:"#5A8A60"}}>✓ {parsed.length} leads ready to import</div>
                <div style={{fontSize:11,color:"#45454A"}}>Showing first 5</div>
              </div>
              <div style={{background:"#111113",border:"1px solid #1C1C1F",borderRadius:10,overflow:"hidden"}}>
                <div style={{display:"grid",gridTemplateColumns:"2fr 1.5fr 1fr 1fr 1fr",background:"#141416",padding:"10px 14px",gap:10}}>
                  {["Name","Address","Equity","Tax Lien","Vacant"].map(h=><div key={h} style={{fontSize:9,letterSpacing:2,color:"#45454A",fontFamily:"'DM Mono',monospace"}}>{h}</div>)}
                </div>
                {preview.map((l,i)=>(
                  <div key={i} style={{display:"grid",gridTemplateColumns:"2fr 1.5fr 1fr 1fr 1fr",padding:"10px 14px",gap:10,borderTop:"1px solid #1C1C1F"}}>
                    <div style={{fontSize:12,color:"#E8E6E1",fontWeight:600}}>{l.name}</div>
                    <div style={{fontSize:11,color:"#6B6B70"}}>{l.address.split(",")[0]}</div>
                    <div style={{fontSize:12,color:"#C9A96E",fontFamily:"'DM Mono',monospace"}}>{l.equity}%</div>
                    <div style={{fontSize:12,color:l.taxLien?"#C06149":"#45454A"}}>{l.taxLien?"YES":"NO"}</div>
                    <div style={{fontSize:12,color:l.vacant?"#C9935A":"#45454A"}}>{l.vacant?"YES":"NO"}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        <div style={{padding:"16px 24px",borderTop:"1px solid #1C1C1F",display:"flex",justifyContent:"flex-end",gap:10}}>
          <button onClick={onClose} style={{background:"none",border:"1px solid #2A2A2E",borderRadius:8,color:"#6B6B70",padding:"10px 20px",cursor:"pointer",fontSize:13}}>Cancel</button>
          {parsed.length>0&&<button onClick={()=>{onImport(parsed);onClose();}} style={{background:"linear-gradient(135deg,#C9A96E,#A8834A)",border:"none",borderRadius:8,color:"#0F0F11",padding:"10px 24px",cursor:"pointer",fontSize:13,fontWeight:800,fontFamily:"'DM Mono',monospace"}}>IMPORT {parsed.length} LEADS →</button>}
        </div>
      </div>
    </div>
  );
}

// ─── Comps Panel ──────────────────────────────────────────────────────────────
function CompsPanel({lead,onUpdate}) {
  const [newComp,setNewComp]=useState({address:"",sqft:"",soldPrice:"",daysAgo:""});
  const [aiArv,setAiArv]=useState(null);
  const [loading,setLoading]=useState(false);
  const [rcLoading,setRcLoading]=useState(false);
  const [rcError,setRcError]=useState("");
  const [manualArv,setManualArv]=useState("");
  const [showManualArv,setShowManualArv]=useState(false);
  const saveManualArv=()=>{
    const val=parseInt(manualArv.replace(/[^0-9]/g,""))||0;
    if(!val)return;
    onUpdate({...lead,arv:{value:val,mao:Math.round(val*0.7),manual:true}});
    setManualArv("");setShowManualArv(false);
  };
  const addComp=()=>{
    if(!newComp.address||!newComp.soldPrice)return;
    const comp={address:newComp.address,sqft:parseInt(newComp.sqft)||0,soldPrice:parseInt(newComp.soldPrice.replace(/[^0-9]/g,""))||0,daysAgo:parseInt(newComp.daysAgo)||0};
    onUpdate({...lead,comps:[...(lead.comps||[]),comp]});
    setNewComp({address:"",sqft:"",soldPrice:"",daysAgo:""});
  };
  const removeComp=(i)=>{const c=[...(lead.comps||[])];c.splice(i,1);onUpdate({...lead,comps:c});};

  const pullRentcastComps=async()=>{
    if(!lead.address||lead.address==="Unknown"){setRcError("Lead needs a valid address.");return;}
    if(!RENTCAST_KEY){setRcError("REACT_APP_RENTCAST_KEY not set in .env");return;}
    setRcLoading(true);setRcError("");
    try {
      const params=new URLSearchParams({address:lead.address,compCount:"10",compRadius:"1",compMaxDaysOld:"90",propertyType:lead.propertyType||"Single Family"});
      if(lead.sqft) params.append("squareFootage",lead.sqft);
      if(lead.bedrooms&&(lead.propertyType==="Single Family"||lead.propertyType==="Multi-Family")) params.append("bedrooms",lead.bedrooms);
      if(lead.bathrooms&&(lead.propertyType==="Single Family"||lead.propertyType==="Multi-Family")) params.append("bathrooms",lead.bathrooms);
      const res=await fetch(`https://api.rentcast.io/v1/avm/value?${params}`,{
        headers:{"X-Api-Key":RENTCAST_KEY,"Content-Type":"application/json"}});
      if(!res.ok){const e=await res.text();setRcError(`Rentcast error: ${res.status} — check address format`);console.error(e);setRcLoading(false);return;}
      const data=await res.json();
      const comps=(data.comparables||[]).map(c=>({
        address:c.formattedAddress||c.address||"Unknown",
        sqft:c.squareFootage||0,
        bedrooms:c.bedrooms||c.bedroomCount||null,
        bathrooms:c.bathrooms||c.bathroomCount||null,
        soldPrice:c.price||c.lastSalePrice||0,
        daysAgo:c.daysOld||c.daysOnMarket||(c.lastSaleDate?Math.round((Date.now()-new Date(c.lastSaleDate))/86400000):0),
        status:c.status||null,
        soldDate:c.removedDate||c.lastSaleDate||null,
        listedDate:c.listedDate||null,
      })).filter(c=>c.soldPrice>0);
      if(!comps.length){setRcError("No sold comps found — try a more specific address.");setRcLoading(false);return;}
      onUpdate({...lead,comps:[...(lead.comps||[]),...comps]});
    }catch(e){setRcError("Failed to fetch: "+e.message);}
    setRcLoading(false);
  };

  const pullRentcastAVM=async()=>{
    if(!lead.address||lead.address==="Unknown"){setRcError("Lead needs a valid address.");return;}
    if(!RENTCAST_KEY){setRcError("REACT_APP_RENTCAST_KEY not set in .env");return;}
    setRcLoading(true);setRcError("");
    try {
      const params=new URLSearchParams({address:lead.address});
      if(lead.sqft) params.append("squareFootage",lead.sqft);
      if(lead.bedrooms&&(lead.propertyType==="Single Family"||lead.propertyType==="Multi-Family")) params.append("bedrooms",lead.bedrooms);
      if(lead.bathrooms&&(lead.propertyType==="Single Family"||lead.propertyType==="Multi-Family")) params.append("bathrooms",lead.bathrooms);
      const res=await fetch(`https://api.rentcast.io/v1/avm/value?${params}`,{
        headers:{"X-Api-Key":RENTCAST_KEY,"Content-Type":"application/json"}});
      if(!res.ok){const e=await res.text();setRcError(`Rentcast AVM error: ${res.status}`);console.error(e);setRcLoading(false);return;}
      const data=await res.json();
      const arv=data.price||data.value||0;
      const mao=Math.round(arv*0.7);
      if(!arv){setRcError("No AVM value returned.");setRcLoading(false);return;}
      setAiArv({arv,mao,pricePerSqft:lead.sqft?Math.round(arv/lead.sqft):0,confidence:"medium",notes:`Rentcast AVM estimate. Range: ${fmt$(data.priceLow||arv*0.9)} – ${fmt$(data.priceHigh||arv*1.1)}`});
    }catch(e){setRcError("Failed to fetch: "+e.message);}
    setRcLoading(false);
  };

  const runAIArv=async()=>{
    if(!lead.comps?.length)return;
    setLoading(true);
    const compsSummary=lead.comps.map(c=>`${c.address}: ${fmt$(c.soldPrice)} (${c.sqft||"?"}sqft, ${c.daysAgo}d ago)`).join("\n");
    try {
      const res=await fetch("https://api.anthropic.com/v1/messages",{method:"POST",
        headers:{"Content-Type":"application/json","x-api-key":ANTHROPIC_KEY,"anthropic-version":"2023-06-01","anthropic-dangerous-direct-browser-access":"true"},
        body:JSON.stringify({model:"claude-sonnet-4-20250514",max_tokens:400,system:"Real estate wholesale analyst. Return ONLY valid JSON.",
          messages:[{role:"user",content:`Subject: ${lead.address}, ${lead.sqft||"?"}sqft${lead.bedrooms?", "+lead.bedrooms+"bd":""} ${lead.bathrooms?lead.bathrooms+"ba":""}\n\nComps:\n${compsSummary}\n\nReturn JSON: {"arv":number,"mao":number,"pricePerSqft":number,"confidence":"low|medium|high","notes":"one line"}`}]})});
      const data=await res.json();
      const text=data.content?.find(b=>b.type==="text")?.text||"{}";
      setAiArv(JSON.parse(text.replace(/```json|```/g,"").trim()));
    }catch(e){console.error(e);}
    setLoading(false);
  };
  const saveArv=()=>{if(!aiArv)return;onUpdate({...lead,arv:{value:aiArv.arv,mao:aiArv.mao}});setAiArv(null);};
  const fs={background:"#0F0F11",border:"1px solid #1C1C1F",borderRadius:6,padding:"8px 10px",color:"#E8E6E1",fontSize:12,outline:"none",width:"100%",boxSizing:"border-box"};
  return (
    <div>
      <div style={{background:"#111113",border:"1px solid #C9A96E22",borderRadius:8,padding:12,marginBottom:14}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:showManualArv?10:0}}>
          <div>
            <div style={{fontSize:9,letterSpacing:3,color:"#C9A96E",fontFamily:"'DM Mono',monospace",marginBottom:2}}>MANUAL ARV OVERRIDE</div>
            {lead.arv?.manual&&<div style={{fontSize:10,color:"#6B6B70"}}>Currently set to <span style={{color:"#C9A96E",fontWeight:700}}>{fmt$(lead.arv.value)}</span> (manual)</div>}
            {lead.arv&&!lead.arv.manual&&<div style={{fontSize:10,color:"#6B6B70"}}>Currently set to <span style={{color:"#C9A96E",fontWeight:700}}>{fmt$(lead.arv.value)}</span> (AI/Rentcast)</div>}
            {!lead.arv&&<div style={{fontSize:10,color:"#45454A"}}>Override AI comps with your own ARV</div>}
          </div>
          <button onClick={()=>setShowManualArv(v=>!v)} style={{background:"#C9A96E14",border:"1px solid #C9A96E33",borderRadius:6,color:"#C9A96E",padding:"5px 12px",fontSize:10,cursor:"pointer",fontFamily:"'DM Mono',monospace",fontWeight:700}}>
            {showManualArv?"CANCEL":"✎ SET ARV"}
          </button>
        </div>
        {showManualArv&&(
          <div style={{display:"flex",gap:8,alignItems:"center"}}>
            <input value={manualArv} onChange={e=>setManualArv(e.target.value)} placeholder="Enter your ARV e.g. 185000"
              style={{flex:1,background:"#0F0F11",border:"1px solid #C9A96E44",borderRadius:6,padding:"8px 10px",color:"#C9A96E",fontSize:14,fontFamily:"'DM Mono',monospace",fontWeight:700,outline:"none",boxSizing:"border-box"}}
              onKeyDown={e=>e.key==="Enter"&&saveManualArv()}/>
            <button onClick={saveManualArv} style={{background:"#C9A96E",border:"none",borderRadius:6,color:"#0F0F11",padding:"8px 16px",fontSize:11,cursor:"pointer",fontWeight:700,fontFamily:"'DM Mono',monospace",whiteSpace:"nowrap"}}>SAVE ARV</button>
          </div>
        )}
      </div>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12}}>
        <div style={{fontSize:9,letterSpacing:3,color:"#45454A",fontFamily:"'DM Mono',monospace"}}>COMPARABLE SALES</div>
        <div style={{display:"flex",gap:6}}>
          <button onClick={pullRentcastAVM} disabled={rcLoading} style={{background:rcLoading?"#1C1C1F":"#5A8A6014",border:"1px solid #5A8A6033",borderRadius:6,color:rcLoading?"#45454A":"#5A8A60",padding:"5px 10px",cursor:rcLoading?"not-allowed":"pointer",fontSize:10,fontFamily:"'DM Mono',monospace",fontWeight:700}}>
            {rcLoading?"...":"⚡ RENTCAST AVM"}
          </button>
          <button onClick={pullRentcastComps} disabled={rcLoading} style={{background:rcLoading?"#1C1C1F":"#C9935A14",border:"1px solid #C9935A33",borderRadius:6,color:rcLoading?"#45454A":"#C9935A",padding:"5px 10px",cursor:rcLoading?"not-allowed":"pointer",fontSize:10,fontFamily:"'DM Mono',monospace",fontWeight:700}}>
            {rcLoading?"...":"📍 PULL COMPS"}
          </button>
          <button onClick={()=>onUpdate({...lead,comps:[]})} style={{background:"#C0614911",border:"1px solid #C0614933",borderRadius:6,color:"#C06149",padding:"5px 10px",cursor:"pointer",fontSize:10,fontFamily:"'DM Mono',monospace",fontWeight:700}}>✕ CLEAR</button>
        </div>
      </div>
      {rcError&&<div style={{background:"#C0614911",border:"1px solid #C0614933",borderRadius:6,padding:"8px 12px",marginBottom:10,color:"#C06149",fontSize:11}}>⚠ {rcError}</div>}
      {(lead.comps||[]).length>0&&(
        <div style={{marginBottom:16}}>
          {lead.comps.map((c,i)=>(
            <div key={i} style={{background:"#111113",border:"1px solid #1C1C1F",borderRadius:8,padding:"12px 14px",marginBottom:8}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:8}}>
                <a href={"https://www.zillow.com/homes/"+encodeURIComponent(c.address)+"_rb/"} target="_blank" rel="noreferrer" style={{fontSize:12,fontWeight:600,color:"#C9A96E",flex:1,marginRight:8,textDecoration:"none",cursor:"pointer"}}>{c.address} ↗</a>
                <div style={{display:"flex",alignItems:"center",gap:8,flexShrink:0}}>
                  <div style={{fontSize:15,fontWeight:800,color:"#5A8A60",fontFamily:"'DM Mono',monospace"}}>{fmt$(c.soldPrice)}</div>
                  <button onClick={()=>removeComp(i)} style={{background:"none",border:"none",color:"#45454A",cursor:"pointer",fontSize:16,lineHeight:1}}>×</button>
                </div>
              </div>
              <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                {c.sqft>0&&<span style={{background:"#1C1C1F",borderRadius:4,padding:"2px 7px",fontSize:10,color:"#8A8885",fontFamily:"'DM Mono',monospace"}}>{c.sqft.toLocaleString()} sqft</span>}
                {c.bedrooms&&<span style={{background:"#1C1C1F",borderRadius:4,padding:"2px 7px",fontSize:10,color:"#8A8885",fontFamily:"'DM Mono',monospace"}}>{c.bedrooms} bd</span>}
                {c.bathrooms&&<span style={{background:"#1C1C1F",borderRadius:4,padding:"2px 7px",fontSize:10,color:"#8A8885",fontFamily:"'DM Mono',monospace"}}>{c.bathrooms} ba</span>}
                {c.sqft>0&&c.soldPrice>0&&<span style={{background:"#1C1C1F",borderRadius:4,padding:"2px 7px",fontSize:10,color:"#C9935A",fontFamily:"'DM Mono',monospace"}}>${Math.round(c.soldPrice/c.sqft)}/sf</span>}
              </div>
            </div>
          ))}
          <button onClick={runAIArv} disabled={loading} style={{width:"100%",background:loading?"#1C1C1F":"#C9A96E14",border:"1px solid #C9A96E33",borderRadius:8,padding:"10px",color:loading?"#45454A":"#C9A96E",fontSize:12,cursor:loading?"not-allowed":"pointer",fontFamily:"'DM Mono',monospace",fontWeight:700}}>
            {loading?"ANALYZING...":"⚡ AI — CALCULATE ARV FROM COMPS"}
          </button>
          {aiArv&&(
            <div style={{background:"#111113",border:"1px solid #C9A96E33",borderRadius:8,padding:14,marginTop:10}}>
              <div style={{fontSize:9,letterSpacing:3,color:"#C9A96E",fontFamily:"'DM Mono',monospace",marginBottom:10}}>AI ANALYSIS · {aiArv.confidence?.toUpperCase()} CONFIDENCE</div>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:10,marginBottom:10}}>
                {[["ARV",fmt$(aiArv.arv),"#C9A96E"],["MAO (70%)",fmt$(aiArv.mao),"#5A8A60"],["$/sqft",`$${aiArv.pricePerSqft}`,"#C9935A"]].map(([k,v,c])=>(
                  <div key={k} style={{background:"#0F0F11",borderRadius:6,padding:"8px 10px"}}><div style={{fontSize:9,color:"#45454A",fontFamily:"'DM Mono',monospace"}}>{k}</div><div style={{fontSize:16,fontWeight:800,color:c,fontFamily:"'DM Mono',monospace"}}>{v}</div></div>
                ))}
              </div>
              <div style={{fontSize:12,color:"#6B6B70",marginBottom:10}}>{aiArv.notes}</div>
              <button onClick={saveArv} style={{background:"#5A8A60",border:"none",borderRadius:6,color:"#0F0F11",padding:"8px 16px",fontSize:11,cursor:"pointer",fontWeight:700,fontFamily:"'DM Mono',monospace"}}>SAVE TO LEAD ↗</button>
            </div>
          )}
        </div>
      )}
      <div style={{background:"#111113",border:"1px solid #2A2A2E",borderRadius:8,padding:14}}>
        <div style={{fontSize:10,color:"#45454A",fontFamily:"'DM Mono',monospace",marginBottom:10}}>ADD COMPARABLE MANUALLY</div>
        <div style={{marginBottom:8}}><input value={newComp.address} onChange={e=>setNewComp(n=>({...n,address:e.target.value}))} placeholder="123 Main St, Houston TX" style={fs}/></div>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:8,marginBottom:10}}>
          <input value={newComp.soldPrice} onChange={e=>setNewComp(n=>({...n,soldPrice:e.target.value}))} placeholder="Sale price $" style={fs}/>
          <input value={newComp.sqft} onChange={e=>setNewComp(n=>({...n,sqft:e.target.value}))} placeholder="Sqft" style={fs} type="number"/>
          <input value={newComp.daysAgo} onChange={e=>setNewComp(n=>({...n,daysAgo:e.target.value}))} placeholder="Days ago" style={fs} type="number"/>
        </div>
        <button onClick={addComp} style={{background:"#C9A96E",border:"none",borderRadius:6,color:"#0F0F11",padding:"8px 16px",fontSize:11,cursor:"pointer",fontWeight:700,fontFamily:"'DM Mono',monospace"}}>+ ADD COMP</button>
      </div>
    </div>
  );
}

// ─── Rehab Config Modal ───────────────────────────────────────────────────────
function RehabConfigModal({config,onSave,onClose,markets=[],onMarketsChange,maoFormula,onMaoFormulaChange}) {
  const [cfg,setCfg]=useState(JSON.parse(JSON.stringify(config)));
  const [currentMarkets,setCurrentMarkets]=useState(markets);
  const [activeMarket,setActiveMarket]=useState("__default__");
  const [newMarketName,setNewMarketName]=useState("");
  const [showAddMarket,setShowAddMarket]=useState(false);
  const [activeTab,setActiveTab]=useState("mao");
  const [maoSaved,setMaoSaved]=useState(false);
  const [rehabSaved,setRehabSaved]=useState(false);

  // cfg structure: { __default__: {light,medium,heavy}, Houston: {light,medium,heavy}, ... }
  // Ensure __default__ always exists
  const safeCfg = cfg.__default__ ? cfg : { __default__: JSON.parse(JSON.stringify(config.__default__ || config)), ...cfg };

  const getMarketCfg=(mkt)=>{
    if(safeCfg[mkt]) return safeCfg[mkt];
    // Clone default for new market
    return JSON.parse(JSON.stringify(safeCfg.__default__));
  };

  const allMarkets=["__default__",...currentMarkets];

  const updateItem=(mkt,tier,idx,field,val)=>{
    setCfg(prev=>{
      const next=JSON.parse(JSON.stringify(prev));
      if(!next[mkt]) next[mkt]=JSON.parse(JSON.stringify(next.__default__));
      next[mkt][tier].items[idx][field]=field==="costPerSqft"?parseFloat(val)||0:val;
      return next;
    });
  };
  const addItem=(mkt,tier)=>{
    setCfg(prev=>{
      const next=JSON.parse(JSON.stringify(prev));
      if(!next[mkt]) next[mkt]=JSON.parse(JSON.stringify(next.__default__));
      next[mkt][tier].items.push({name:"New Item",costPerSqft:1.00});
      return next;
    });
  };
  const removeItem=(mkt,tier,idx)=>{
    setCfg(prev=>{
      const next=JSON.parse(JSON.stringify(prev));
      if(!next[mkt]) next[mkt]=JSON.parse(JSON.stringify(next.__default__));
      next[mkt][tier].items.splice(idx,1);
      return next;
    });
  };
  const addMarket=()=>{
    const name=newMarketName.trim();
    if(!name||currentMarkets.includes(name))return;
    const updated=[...currentMarkets,name];
    setCurrentMarkets(updated);
    onMarketsChange(updated);
    setCfg(prev=>({...prev,[name]:JSON.parse(JSON.stringify(prev.__default__))}));
    setActiveMarket(name);
    setNewMarketName("");
    setShowAddMarket(false);
  };
  const removeMarket=(mkt)=>{
    const updated=currentMarkets.filter(m=>m!==mkt);
    setCurrentMarkets(updated);
    onMarketsChange(updated);
    setCfg(prev=>{const next={...prev};delete next[mkt];return next;});
    setActiveMarket("__default__");
  };

  const tierCfg=getMarketCfg(activeMarket);

  return (
    <div style={{position:"fixed",inset:0,background:"#000C",zIndex:200,display:"flex",alignItems:"center",justifyContent:"center"}} onClick={e=>e.target===e.currentTarget&&onClose()}>
      <div style={{background:"#111113",border:"1px solid #1C1C1F",borderRadius:14,width:"90vw",maxWidth:1100,height:"88vh",overflow:"hidden",display:"flex",flexDirection:"column"}}>

        {/* ── Header ── */}
        <div style={{padding:"18px 28px 0",display:"flex",justifyContent:"space-between",alignItems:"flex-start",flexShrink:0}}>
          <div>
            <div style={{fontSize:10,letterSpacing:3,color:"#C9935A",fontFamily:"'DM Mono',monospace",marginBottom:4}}>SETTINGS</div>
            <div style={{fontSize:20,fontWeight:800,color:"#E8E6E1"}}>Cost Configuration</div>
          </div>
          <button onClick={onClose} style={{background:"none",border:"none",color:"#6B6B70",cursor:"pointer",fontSize:22,marginTop:4}}>✕</button>
        </div>

        {/* ── Top-level tabs ── */}
        <div style={{display:"flex",gap:0,padding:"0 28px",marginTop:16,borderBottom:"1px solid #1C1C1F",flexShrink:0}}>
          {[{id:"mao",label:"📐 MAO Formula"},{id:"rehab",label:"🔨 Rehab Calculator"}].map(t=>(
            <button key={t.id} onClick={()=>setActiveTab(t.id)} style={{background:"none",border:"none",borderBottom:activeTab===t.id?"2px solid #C9935A":"2px solid transparent",padding:"10px 20px",fontSize:12,cursor:"pointer",fontFamily:"'DM Mono',monospace",fontWeight:activeTab===t.id?700:400,color:activeTab===t.id?"#C9935A":"#45454A",whiteSpace:"nowrap"}}>
              {t.label}
            </button>
          ))}
        </div>

        {/* ── TAB 1: MAO Formula ── */}
        {activeTab==="mao"&&(
          <div style={{display:"flex",flexDirection:"column",flex:1,minHeight:0}}>
            <div style={{overflowY:"auto",padding:"20px 28px",flex:1,minHeight:0}}>
              <div style={{fontSize:11,color:"#45454A",fontFamily:"'DM Mono',monospace",marginBottom:16,lineHeight:1.6}}>
                Set your offer formula per property type. MAO = ({"{"}ARV or FMV{"}"} × Multiplier%) − Closing Costs − Repairs − Min Profit
              </div>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:16}}>
                {[
                  {key:"singleFamily",label:"🏠 Single Family",hasRepairs:true},
                  {key:"land",label:"🌿 Land",hasRepairs:false},
                  {key:"multiFamily",label:"🏢 Multi-Family",hasRepairs:true},
                  {key:"commercial",label:"🏪 Commercial",hasRepairs:true},
                ].map(({key,label,hasRepairs})=>{
                  const f=(maoFormula&&maoFormula[key])||{multiplier:70,closingCosts:0,minProfit:0,assignmentFee:0};
                  const update=(field,val)=>onMaoFormulaChange&&onMaoFormulaChange({...maoFormula,[key]:{...f,[field]:parseFloat(val)||0}});
                  return(
                    <div key={key} style={{background:"#111113",border:"1px solid #1C1C1F",borderRadius:10,padding:16}}>
                      <div style={{fontSize:11,fontWeight:700,color:"#C4C2BD",fontFamily:"'DM Mono',monospace",marginBottom:14}}>{label}</div>
                      <div style={{marginBottom:10}}>
                        <div style={{fontSize:9,color:"#45454A",fontFamily:"'DM Mono',monospace",marginBottom:4}}>{hasRepairs?"ARV":"FMV"} MULTIPLIER %</div>
                        <input type="number" min="30" max="95" step="1" value={f.multiplier||70}
                          onChange={e=>update("multiplier",e.target.value)}
                          style={{width:"100%",background:"#1C1C1F",border:"1px solid #5A8A6033",borderRadius:6,padding:"8px 10px",color:"#5A8A60",fontSize:18,fontFamily:"'DM Mono',monospace",fontWeight:800,boxSizing:"border-box"}}/>
                      </div>
                      {hasRepairs&&<>
                        <div style={{marginBottom:10}}>
                          <div style={{fontSize:9,color:"#45454A",fontFamily:"'DM Mono',monospace",marginBottom:4}}>CLOSING COSTS %</div>
                          <input type="number" min="0" max="10" step="0.5" value={f.closingCosts||0}
                            onChange={e=>update("closingCosts",e.target.value)}
                            style={{width:"100%",background:"#1C1C1F",border:"1px solid #C9935A33",borderRadius:6,padding:"8px 10px",color:"#C9935A",fontSize:14,fontFamily:"'DM Mono',monospace",fontWeight:700,boxSizing:"border-box"}}/>
                        </div>
                        <div style={{marginBottom:10}}>
                          <div style={{fontSize:9,color:"#45454A",fontFamily:"'DM Mono',monospace",marginBottom:4}}>MIN PROFIT $</div>
                          <input type="number" min="0" step="1000" value={f.minProfit||0}
                            onChange={e=>update("minProfit",e.target.value)}
                            style={{width:"100%",background:"#1C1C1F",border:"1px solid #C9A96E33",borderRadius:6,padding:"8px 10px",color:"#C9A96E",fontSize:14,fontFamily:"'DM Mono',monospace",fontWeight:700,boxSizing:"border-box"}}/>
                        </div>
                      </>}
                      <div style={{marginBottom:10}}>
                        <div style={{fontSize:9,color:"#45454A",fontFamily:"'DM Mono',monospace",marginBottom:4}}>ASSIGNMENT FEE $</div>
                        <input type="number" min="0" step="500" value={f.assignmentFee||0}
                          onChange={e=>update("assignmentFee",e.target.value)}
                          style={{width:"100%",background:"#1C1C1F",border:"1px solid #B87AA833",borderRadius:6,padding:"8px 10px",color:"#B87AA8",fontSize:14,fontFamily:"'DM Mono',monospace",fontWeight:700,boxSizing:"border-box"}}/>
                      </div>
                      <div style={{background:"#111113",borderRadius:6,padding:"8px 10px",fontSize:10,color:"#45454A",fontFamily:"'DM Mono',monospace",marginTop:6}}>
                        MAO = {hasRepairs?"ARV":"FMV"} × <span style={{color:"#5A8A60"}}>{f.multiplier||70}%</span>
                        {hasRepairs&&(f.closingCosts||0)>0&&<> − <span style={{color:"#C9935A"}}>{f.closingCosts}% closing</span></>}
                        {hasRepairs&&<> − repairs</>}
                        {hasRepairs&&(f.minProfit||0)>0&&<> − <span style={{color:"#C9A96E"}}>${(f.minProfit||0).toLocaleString()} profit</span></>}
                        {(f.assignmentFee||0)>0&&<> − <span style={{color:"#B87AA8"}}>${(f.assignmentFee||0).toLocaleString()} fee</span></>}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
            <div style={{padding:"14px 28px",borderTop:"1px solid #1C1C1F",display:"flex",justifyContent:"flex-end",gap:10,flexShrink:0}}>
              <button onClick={onClose} style={{background:"none",border:"1px solid #2A2A2E",borderRadius:8,color:"#6B6B70",padding:"9px 20px",cursor:"pointer",fontSize:13}}>Close</button>
              <button onClick={()=>{setMaoSaved(true);setTimeout(()=>setMaoSaved(false),2000);}}
                style={{background:maoSaved?"#5A8A60":"linear-gradient(135deg,#5A8A60,#16A34A)",border:"none",borderRadius:8,color:"#0F0F11",padding:"9px 24px",cursor:"pointer",fontSize:13,fontWeight:800,fontFamily:"'DM Mono',monospace",transition:"background .3s"}}>
                {maoSaved?"✓ SAVED":"SAVE MAO FORMULA ↗"}
              </button>
            </div>
          </div>
        )}

        {/* ── TAB 2: Rehab Calculator ── */}
        {activeTab==="rehab"&&(
          <div style={{display:"flex",flexDirection:"column",flex:1,minHeight:0}}>

            {/* Market tabs */}
            <div style={{display:"flex",alignItems:"center",gap:0,padding:"0 24px",borderBottom:"1px solid #1C1C1F",overflowX:"auto",flexShrink:0}}>
              {allMarkets.map(mkt=>(
                <div key={mkt} style={{display:"flex",alignItems:"center",gap:0}}>
                  <button onClick={()=>setActiveMarket(mkt)}
                    style={{background:"none",border:"none",borderBottom:activeMarket===mkt?"2px solid #C9935A":"2px solid transparent",padding:"10px 14px",fontSize:11,cursor:"pointer",fontFamily:"'DM Mono',monospace",color:activeMarket===mkt?"#C9935A":"#45454A",whiteSpace:"nowrap",fontWeight:activeMarket===mkt?700:400}}>
                    {mkt==="__default__"?"DEFAULT":mkt.toUpperCase()}
                  </button>
                  {mkt!=="__default__"&&(
                    <button onClick={()=>removeMarket(mkt)} style={{background:"none",border:"none",color:"#45454A",cursor:"pointer",fontSize:13,padding:"0 4px",lineHeight:1}} title="Remove market">×</button>
                  )}
                </div>
              ))}
              {showAddMarket
                ? <div style={{display:"flex",alignItems:"center",gap:6,padding:"6px 8px"}}>
                    <input autoFocus value={newMarketName} onChange={e=>setNewMarketName(e.target.value)} onKeyDown={e=>{if(e.key==="Enter")addMarket();if(e.key==="Escape"){setShowAddMarket(false);setNewMarketName("");}}}
                      placeholder="Market name…" style={{background:"#1C1C1F",border:"1px solid #2A2A2E",borderRadius:6,padding:"4px 8px",color:"#E8E6E1",fontSize:11,outline:"none",width:120}}/>
                    <button onClick={addMarket} style={{background:"#C9935A",border:"none",borderRadius:5,color:"#0F0F11",padding:"4px 8px",fontSize:10,cursor:"pointer",fontWeight:700,fontFamily:"'DM Mono',monospace"}}>ADD</button>
                    <button onClick={()=>{setShowAddMarket(false);setNewMarketName("");}} style={{background:"none",border:"none",color:"#45454A",cursor:"pointer",fontSize:14}}>✕</button>
                  </div>
                : <button onClick={()=>setShowAddMarket(true)} style={{background:"none",border:"1px dashed #2A2A2E",borderRadius:6,color:"#45454A",padding:"4px 10px",cursor:"pointer",fontSize:10,fontFamily:"'DM Mono',monospace",margin:"6px 8px",whiteSpace:"nowrap"}}>+ ADD MARKET</button>
              }
            </div>

            {activeMarket!=="__default__"&&(
              <div style={{padding:"7px 24px",background:"#C9935A0A",borderBottom:"1px solid #C9935A22",fontSize:11,color:"#C9935A99",fontFamily:"'DM Mono',monospace",flexShrink:0}}>
                ⚡ Overrides for <strong style={{color:"#C9935A"}}>{activeMarket}</strong> — falls back to DEFAULT if not set.
              </div>
            )}

            {/* Tier columns */}
            <div style={{overflowY:"auto",padding:20,flex:1,minHeight:0}}>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:16}}>
                {["light","medium","heavy"].map(tier=>{
                  const tc=tierCfg[tier]||DEFAULT_REHAB[tier];
                  return(
                  <div key={tier} style={{background:"#111113",border:`1px solid ${tc.color}33`,borderRadius:10}}>
                    <div style={{padding:"12px 16px",background:tc.color+"14",borderBottom:`1px solid ${tc.color}33`,borderRadius:"10px 10px 0 0"}}>
                      <div style={{fontSize:11,fontWeight:700,color:tc.color,fontFamily:"'DM Mono',monospace",letterSpacing:2}}>{tc.label.toUpperCase()}</div>
                    </div>
                    <div style={{padding:14}}>
                      {tc.items.map((item,i)=>(
                        <div key={i} style={{marginBottom:10}}>
                          <input value={item.name} onChange={e=>updateItem(activeMarket,tier,i,"name",e.target.value)} style={{width:"100%",background:"#1C1C1F",border:"1px solid #2A2A2E",borderRadius:5,padding:"5px 8px",color:"#C4C2BD",fontSize:11,marginBottom:5,boxSizing:"border-box"}}/>
                          <div style={{display:"flex",gap:6,alignItems:"center"}}>
                            <input type="number" step="0.25" value={item.costPerSqft} onChange={e=>updateItem(activeMarket,tier,i,"costPerSqft",e.target.value)} style={{flex:1,background:"#1C1C1F",border:"1px solid #2A2A2E",borderRadius:5,padding:"5px 8px",color:tc.color,fontSize:11,fontFamily:"'DM Mono',monospace"}}/>
                            <span style={{fontSize:10,color:"#45454A",fontFamily:"'DM Mono',monospace",whiteSpace:"nowrap"}}>/sqft</span>
                            <button onClick={()=>removeItem(activeMarket,tier,i)} style={{background:"none",border:"none",color:"#45454A",cursor:"pointer",fontSize:14,padding:"0 4px"}}>×</button>
                          </div>
                        </div>
                      ))}
                      <button onClick={()=>addItem(activeMarket,tier)} style={{width:"100%",background:"none",border:`1px dashed ${tc.color}44`,borderRadius:6,color:tc.color,padding:"6px",fontSize:10,cursor:"pointer",fontFamily:"'DM Mono',monospace"}}>+ ADD LINE</button>
                      <div style={{marginTop:10,paddingTop:10,borderTop:"1px solid #1C1C1F",fontSize:10,color:"#45454A",fontFamily:"'DM Mono',monospace"}}>
                        TOTAL: <span style={{color:tc.color}}>${tc.items.reduce((s,x)=>s+x.costPerSqft,0).toFixed(2)}/sqft</span>
                      </div>
                    </div>
                  </div>
                );})}
              </div>
            </div>

            <div style={{padding:"14px 28px",borderTop:"1px solid #1C1C1F",display:"flex",justifyContent:"flex-end",gap:10,flexShrink:0}}>
              <button onClick={onClose} style={{background:"none",border:"1px solid #2A2A2E",borderRadius:8,color:"#6B6B70",padding:"9px 20px",cursor:"pointer",fontSize:13}}>Close</button>
              <button onClick={()=>{onSave(cfg,currentMarkets);setRehabSaved(true);setTimeout(()=>setRehabSaved(false),2000);}}
                style={{background:rehabSaved?"#C9935A":"linear-gradient(135deg,#C9935A,#A87040)",border:"none",borderRadius:8,color:"#0F0F11",padding:"9px 24px",cursor:"pointer",fontSize:13,fontWeight:800,fontFamily:"'DM Mono',monospace",transition:"background .3s"}}>
                {rehabSaved?"✓ SAVED":"SAVE REHAB CONFIG ↗"}
              </button>
            </div>
          </div>
        )}

      </div>
    </div>
  );
}

// ─── AI Panel ─────────────────────────────────────────────────────────────────
function AIPanel({lead,rehabConfig,onClose,onUpdate,storedPhotos=[]}) {
  const [tab,setTab]=useState("sms");
  const [loading,setLoading]=useState(false);
  const [result,setResult]=useState(null);
  const [streetViewImg,setStreetViewImg]=useState(null);
  const [photoImgs,setPhotoImgs]=useState([]);
  const photoInputRef=useRef(null);
  const stage=stageOf(lead.stage);

  const callClaude=async(system,user,images=[])=>{
    setLoading(true);setResult(null);
    try {
      const imgBlocks=(images||[]).map(b=>({type:"image",source:{type:"base64",media_type:typeof b==="object"?(b.mimeType||b.mediaType||"image/jpeg"):"image/jpeg",data:typeof b==="object"?b.data:b}}));
      const content=imgBlocks.length>0?[...imgBlocks,{type:"text",text:user}]:user;
      const res=await fetch("https://api.anthropic.com/v1/messages",{method:"POST",
        headers:{"Content-Type":"application/json","x-api-key":ANTHROPIC_KEY,"anthropic-version":"2023-06-01","anthropic-dangerous-direct-browser-access":"true"},
        body:JSON.stringify({model:"claude-sonnet-4-20250514",max_tokens:1000,system,messages:[{role:"user",content}]})});
      const data=await res.json();
      if(data.error){setResult("API Error: "+data.error.message);setLoading(false);return;}
      setResult(data.content?.filter(b=>b.type==="text").map(b=>b.text).join("\n")||"No response.");
    }catch(e){setResult("Error: "+e.message);}
    setLoading(false);
  };

  const fetchStreetView=async()=>{
    const url=`https://maps.googleapis.com/maps/api/streetview?size=640x400&location=${encodeURIComponent(lead.address)}&key=${GOOGLE_SV_KEY}`;
    try{const res=await fetch(url);const blob=await res.blob();const reader=new FileReader();reader.onload=()=>setStreetViewImg(reader.result.split(",")[1]);reader.readAsDataURL(blob);}catch(e){console.error(e);}
  };

  const runSMS=()=>{
    const hist=lead.smsHistory.map(m=>`${m.from==="in"?lead.name:"You"}: ${m.text}`).join("\n");
    callClaude(`Expert real estate wholesaler. Draft natural brief SMS under 160 chars. No salesy language. Build rapport, move toward cash offer.`,
      `Lead: ${lead.name} | ${lead.address} | Stage: ${stage.label} | Equity: ${lead.equity}% | Notes: ${lead.notes}\n\nConversation:\n${hist||"No messages yet"}\n\nDraft next best SMS. Output ONLY message text.`);
  };

  const runScore=()=>callClaude(`Wholesale lead scoring expert. Score 0-100. Be direct.`,
    `Score lead:\nName: ${lead.name}\nAddress: ${lead.address}\nEquity: ${lead.equity}%\nTax Lien: ${lead.taxLien}\nVacant: ${lead.vacant}\nStage: ${stage.label}\nNotes: ${lead.notes}\n\nReturn:\n1. Score (0-100)\n2. HOT LEAD: YES/NO\n3. Top 3 motivation signals\n4. Urgency: Low/Medium/High/Critical\n5. Best next action`);

  const compressImg=async(b64,mimeType="image/jpeg")=>{
    return new Promise(res=>{
      const img=new Image();
      img.onload=()=>{
        const MAX=1200;
        let w=img.width,h=img.height;
        if(w>MAX||h>MAX){if(w>h){h=Math.round(h*MAX/w);w=MAX;}else{w=Math.round(w*MAX/h);h=MAX;}}
        const canvas=document.createElement("canvas");
        canvas.width=w;canvas.height=h;
        canvas.getContext("2d").drawImage(img,0,0,w,h);
        const out=canvas.toDataURL("image/jpeg",0.75).split(",")[1];
        res({data:out,mimeType:"image/jpeg"});
      };
      img.src=`data:${mimeType};base64,${b64}`;
    });
  };

  const runCondition=async()=>{
    const allImgs=[];
    if(storedPhotos.length>0){
      const toB64fromUrl=async(url)=>{try{const r=await fetch(url);const blob=await r.blob();return new Promise((res,rej)=>{const fr=new FileReader();fr.onload=()=>{const parts=fr.result.split(",");res({data:parts[1],mimeType:parts[0].match(/:(.*?);/)?.[1]||"image/jpeg"});};fr.onerror=rej;fr.readAsDataURL(blob);});}catch(e){return null;}};
      const raw=await Promise.all(storedPhotos.slice(0,4).map(p=>{
        if(typeof p==="object"&&p.data)return Promise.resolve({data:p.data,mimeType:p.mimeType||"image/jpeg"});
        if(typeof p==="string")return toB64fromUrl(p);
        return Promise.resolve(null);
      }));
      const compressed=await Promise.all(raw.filter(Boolean).map(p=>compressImg(p.data,p.mimeType)));
      allImgs.push(...compressed);
    }
    if(photoImgs.length>0){
      const compressed=await Promise.all(photoImgs.map(p=>compressImg(typeof p==="object"?p.data:p,typeof p==="object"?p.mimeType:"image/jpeg")));
      allImgs.push(...compressed);
    }
    if(!streetViewImg)await fetchStreetView();
    if(streetViewImg)allImgs.push(streetViewImg);
    if(allImgs.length===0){setResult("No images available. Upload photos or wait for Street View.");return;}
    const cfg=rehabConfig.__default__||rehabConfig;
    const tiers=["light","medium","heavy"].map(t=>`${(cfg[t]||{label:t}).label}: $${((cfg[t]||{items:[]}).items||[]).reduce((s,i)=>s+i.costPerSqft,0).toFixed(2)}/sqft`).join(", ");
    const totalUploaded=storedPhotos.length+photoImgs.length;
    const note=totalUploaded>0&&streetViewImg?"Analyzing "+totalUploaded+" property photo(s) + street view exterior":totalUploaded>0?"Analyzing "+totalUploaded+" property photo(s)":"Analyzing street view exterior only";
    callClaude("You are a property condition analyst for real estate wholesalers. Analyze all provided images together for a complete assessment.",
      `Property: ${lead.address} | Sqft: ${lead.sqft||"unknown"} (${note})\nRehab tiers: ${tiers}\n\nProvide:\n1. Condition Grade (A/B/C/D)\n2. Recommended rehab tier (light/medium/heavy)\n3. Key issues (roof, foundation, kitchen, baths, systems, landscaping)\n4. Estimated rehab cost range\n5. Top 3 renovation priorities`,allImgs);
  };

  const runFollowUp=()=>callClaude(`Wholesale follow-up expert. Create tactical actionable plans.`,
    `Lead: ${lead.name} | ${lead.address}\nStage: ${stage.label} | Score: ${lead.score}\nNotes: ${lead.notes}\nLast contact: ${lead.lastContact||"never"}\n\nCreate 3-step follow-up sequence with timing, channel, and exact message.`);

  const applyDraft=()=>onUpdate({...lead,smsHistory:[...lead.smsHistory,{from:"out",text:result.trim(),time:"Just now"}]});
  const applyCondition=()=>{const t=result.match(/\b(light|medium|heavy)\b/i);onUpdate({...lead,rehabTier:t?t[1].toLowerCase():null,conditionReport:result});};
  const applyScore=()=>{const s=result.match(/\b(\d{1,3})\b/);const h=result.match(/HOT LEAD:\s*(YES|NO)/i);onUpdate({...lead,score:s?Math.min(100,parseInt(s[1])):lead.score,hot:h?h[1].toUpperCase()==="YES":lead.hot});};

  const TABS=[{id:"sms",icon:"💬",label:"Draft SMS",run:runSMS},{id:"score",icon:"⚡",label:"Score",run:runScore},{id:"condition",icon:"🏠",label:"Condition",run:runCondition},{id:"followup",icon:"📅",label:"Follow-Up",run:runFollowUp}];

  return (
    <div style={{position:"fixed",right:0,top:0,bottom:0,width:440,background:"#0F0F11",borderLeft:"1px solid #1C1C1F",display:"flex",flexDirection:"column",zIndex:100}}>
      <div style={{padding:"20px 22px 16px",borderBottom:"1px solid #1C1C1F",display:"flex",justifyContent:"space-between",alignItems:"flex-start"}}>
        <div>
          <div style={{fontSize:9,letterSpacing:4,color:"#C9A96E",fontFamily:"'DM Mono',monospace",marginBottom:4}}>AI AGENT ◈ ACTIVE</div>
          <div style={{fontSize:17,fontWeight:800,color:"#E8E6E1"}}>{lead.name}</div>
          <div style={{fontSize:11,color:"#45454A",marginTop:2}}>{lead.address}</div>
        </div>
        <button onClick={onClose} style={{background:"none",border:"none",color:"#45454A",cursor:"pointer",fontSize:20}}>✕</button>
      </div>
      <div style={{display:"flex",borderBottom:"1px solid #1C1C1F"}}>
        {TABS.map(t=><button key={t.id} onClick={()=>{setTab(t.id);setResult(null);}} style={{background:"none",border:"none",padding:"10px 12px",fontSize:11,cursor:"pointer",fontFamily:"'DM Mono',monospace",whiteSpace:"nowrap",color:tab===t.id?"#C9A96E":"#45454A",borderBottom:tab===t.id?"2px solid #C9A96E":"2px solid transparent"}}>{t.icon} {t.label}</button>)}
      </div>
      <div style={{flex:1,overflowY:"auto",padding:18}}>
        {tab==="sms"&&<div style={{marginBottom:16}}><div style={{fontSize:9,letterSpacing:3,color:"#45454A",fontFamily:"'DM Mono',monospace",marginBottom:10}}>CONVERSATION</div>{lead.smsHistory.map((m,i)=><Bubble key={i} msg={m}/>)}</div>}
        {tab==="condition"&&(
          <div style={{marginBottom:16}}>
            <input ref={photoInputRef} type="file" accept="image/*" multiple style={{display:"none"}} onChange={async e=>{
              const files=Array.from(e.target.files);
              if(!files.length)return;
              const toB64=f=>new Promise((res,rej)=>{const r=new FileReader();r.onload=()=>res({data:r.result.split(",")[1],mediaType:f.type||"image/jpeg"});r.onerror=rej;r.readAsDataURL(f);});
              const imgs=await Promise.all(files.slice(0,6).map(toB64));
              setPhotoImgs(prev=>[...prev,...imgs].slice(0,6));
            }}/>
            <button onClick={()=>photoInputRef.current?.click()} style={{width:"100%",background:"#141416",border:"1px dashed #2A2A2E",borderRadius:8,padding:"14px",cursor:"pointer",marginBottom:10,display:"flex",alignItems:"center",justifyContent:"center",gap:10}}>
              <span style={{fontSize:20}}>📷</span>
              <div style={{textAlign:"left"}}>
                <div style={{fontSize:12,fontWeight:700,color:"#C4C2BD"}}>Upload property photos</div>
                <div style={{fontSize:10,color:"#6B6B70"}}>Interior + exterior · up to 6 · combined with street view</div>
              </div>
            </button>
            {photoImgs.length>0&&(
              <div style={{marginBottom:10}}>
                <div style={{display:"flex",gap:6,flexWrap:"wrap",marginBottom:6}}>
                  {photoImgs.map((img,i)=>(
                    <div key={i} style={{position:"relative"}}>
                      <img src={`data:${img.mediaType||"image/jpeg"};base64,${img.data||img}`} style={{width:72,height:54,objectFit:"cover",borderRadius:6,border:"1px solid #1C1C1F",display:"block"}}/>
                      <button onClick={()=>setPhotoImgs(p=>p.filter((_,j)=>j!==i))} style={{position:"absolute",top:-5,right:-5,background:"#C06149",border:"none",borderRadius:"50%",width:16,height:16,cursor:"pointer",fontSize:9,color:"#fff",lineHeight:"16px",padding:0}}>✕</button>
                    </div>
                  ))}
                </div>
                <div style={{fontSize:10,color:"#5A8A60"}}>{photoImgs.length} photo{photoImgs.length>1?"s":""} ready — AI analyzes with street view</div>
              </div>
            )}
            {streetViewImg&&<div style={{borderRadius:8,overflow:"hidden",border:"1px solid #1C1C1F",marginBottom:10}}><img src={`data:image/jpeg;base64,${streetViewImg}`} alt="Street View" style={{width:"100%",display:"block",maxHeight:150,objectFit:"cover"}}/><div style={{padding:"6px 10px",background:"#141416",fontSize:10,color:"#45454A"}}>📍 Street view · {lead.address}</div></div>}
            {!streetViewImg&&<div style={{fontSize:10,color:"#45454A",padding:"8px 10px",background:"#141416",borderRadius:6,marginBottom:8}}>Street view loads automatically when you run analysis</div>}
            {lead.conditionReport&&<div style={{background:"#141416",border:"1px solid #5A8A6033",borderRadius:8,padding:12,marginTop:8}}><div style={{fontSize:9,letterSpacing:3,color:"#5A8A60",fontFamily:"'DM Mono',monospace",marginBottom:6}}>SAVED CONDITION REPORT</div><div style={{fontSize:11,color:"#8A8885",lineHeight:1.6,whiteSpace:"pre-wrap"}}>{lead.conditionReport}</div></div>}
          </div>
        )}
        {result&&(
          <div style={{background:"#111113",border:"1px solid #1E3A5F",borderRadius:10,padding:16,marginTop:8}}>
            <div style={{fontSize:9,letterSpacing:3,color:"#C9A96E",fontFamily:"'DM Mono',monospace",marginBottom:10}}>◈ AI OUTPUT</div>
            <div style={{fontSize:13,color:"#C4C2BD",lineHeight:1.75,whiteSpace:"pre-wrap"}}>{result}</div>
            <div style={{display:"flex",gap:8,marginTop:14}}>
              {tab==="sms"&&<button onClick={applyDraft} style={{background:"#C9A96E",border:"none",borderRadius:6,color:"#0F0F11",padding:"8px 16px",fontSize:11,cursor:"pointer",fontWeight:700,fontFamily:"'DM Mono',monospace"}}>↗ USE DRAFT</button>}
              {tab==="condition"&&<button onClick={applyCondition} style={{background:"#C9935A",border:"none",borderRadius:6,color:"#0F0F11",padding:"8px 16px",fontSize:11,cursor:"pointer",fontWeight:700,fontFamily:"'DM Mono',monospace"}}>↗ SAVE CONDITION</button>}
              {tab==="score"&&<button onClick={applyScore} style={{background:"#9B7ED4",border:"none",borderRadius:6,color:"#0F0F11",padding:"8px 16px",fontSize:11,cursor:"pointer",fontWeight:700,fontFamily:"'DM Mono',monospace"}}>↗ SAVE SCORE</button>}
            </div>
          </div>
        )}
        {loading&&<div style={{display:"flex",alignItems:"center",gap:10,padding:16,color:"#C9A96E",fontSize:13}}><div style={{width:8,height:8,borderRadius:"50%",background:"#C9A96E",animation:"aiPulse 1s infinite"}}/>Agent processing...</div>}
      </div>
      <div style={{padding:"14px 18px",borderTop:"1px solid #1C1C1F"}}>
        <button onClick={TABS.find(t=>t.id===tab)?.run} disabled={loading} style={{width:"100%",border:"none",borderRadius:8,background:loading?"#1C1C1F":"linear-gradient(135deg,#C9A96E,#A8834A)",color:loading?"#45454A":"#0F0F11",padding:"12px",fontSize:12,fontWeight:800,cursor:loading?"not-allowed":"pointer",fontFamily:"'DM Mono',monospace",letterSpacing:1.5}}>
          {loading?"GENERATING...":`▶ RUN ${TABS.find(t=>t.id===tab)?.label?.toUpperCase()}`}
        </button>
      </div>
      <style>{`@keyframes aiPulse{0%,100%{opacity:1}50%{opacity:.2}}`}</style>
    </div>
  );
}

// ─── Follow-Up Picker ─────────────────────────────────────────────────────────
function FollowUpPicker({onAdd}) {
  const now=new Date();
  const [viewYear,setViewYear]=useState(now.getFullYear());
  const [viewMonth,setViewMonth]=useState(now.getMonth());
  const [selDate,setSelDate]=useState(null);
  const [hour,setHour]=useState("9");
  const [minute,setMinute]=useState("00");
  const [ampm,setAmpm]=useState("AM");
  const [note,setNote]=useState("");
  const [showCal,setShowCal]=useState(false);

  const MONTHS=["January","February","March","April","May","June","July","August","September","October","November","December"];
  const DAYS=["Su","Mo","Tu","We","Th","Fr","Sa"];

  const daysInMonth=(y,m)=>new Date(y,m+1,0).getDate();
  const firstDay=(y,m)=>new Date(y,m,1).getDay();

  const prevMonth=()=>{if(viewMonth===0){setViewMonth(11);setViewYear(y=>y-1);}else setViewMonth(m=>m-1);};
  const nextMonth=()=>{if(viewMonth===11){setViewMonth(0);setViewYear(y=>y+1);}else setViewMonth(m=>m+1);};

  const isToday=(d)=>{const t=new Date();return d===t.getDate()&&viewMonth===t.getMonth()&&viewYear===t.getFullYear();};
  const isPast=(d)=>new Date(viewYear,viewMonth,d)<new Date(now.getFullYear(),now.getMonth(),now.getDate());

  const handleAdd=()=>{
    if(!selDate)return;
    let h=parseInt(hour)||9;
    if(ampm==="PM"&&h!==12)h+=12;
    if(ampm==="AM"&&h===12)h=0;
    const d=new Date(viewYear,viewMonth,selDate,h,parseInt(minute)||0);
    const due=d.toLocaleDateString("en-US",{month:"short",day:"numeric",year:"numeric",hour:"numeric",minute:"2-digit"});
    onAdd({due,note:note||"Follow up",done:false,ts:d.toISOString()});
    setSelDate(null);setNote("");setHour("9");setMinute("00");setAmpm("AM");setShowCal(false);
  };

  const calDays=[];
  const fd=firstDay(viewYear,viewMonth);
  const dim=daysInMonth(viewYear,viewMonth);
  for(let i=0;i<fd;i++)calDays.push(null);
  for(let i=1;i<=dim;i++)calDays.push(i);

  const selectedLabel=selDate?new Date(viewYear,viewMonth,selDate).toLocaleDateString("en-US",{month:"short",day:"numeric",year:"numeric"}):null;

  return(
    <div style={{background:"#111113",border:"1px dashed #2A2A2E",borderRadius:8,padding:12}}>
      <div style={{fontSize:10,color:"#45454A",fontFamily:"'DM Mono',monospace",marginBottom:10}}>+ ADD FOLLOW-UP</div>

      {/* Date trigger */}
      <button onClick={()=>setShowCal(!showCal)}
        style={{width:"100%",background:"#1C1C1F",border:`1px solid ${selDate?"#C9935A44":"#2A2A2E"}`,borderRadius:6,padding:"8px 12px",color:selDate?"#C9935A":"#45454A",fontSize:12,cursor:"pointer",textAlign:"left",marginBottom:8,fontFamily:"'DM Mono',monospace",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
        <span>{selectedLabel||"📅 Select date..."}</span>
        <span style={{fontSize:10}}>{showCal?"▲":"▼"}</span>
      </button>

      {/* Calendar */}
      {showCal&&(
        <div style={{background:"#111113",border:"1px solid #1C1C1F",borderRadius:10,padding:14,marginBottom:10}}>
          {/* Month nav */}
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10}}>
            <button onClick={prevMonth} style={{background:"none",border:"none",color:"#45454A",cursor:"pointer",fontSize:16,padding:"0 6px"}}>‹</button>
            <div style={{fontSize:11,fontWeight:700,color:"#C4C2BD",fontFamily:"'DM Mono',monospace"}}>{MONTHS[viewMonth]} {viewYear}</div>
            <button onClick={nextMonth} style={{background:"none",border:"none",color:"#45454A",cursor:"pointer",fontSize:16,padding:"0 6px"}}>›</button>
          </div>
          {/* Day headers */}
          <div style={{display:"grid",gridTemplateColumns:"repeat(7,1fr)",gap:2,marginBottom:4}}>
            {DAYS.map(d=><div key={d} style={{textAlign:"center",fontSize:9,color:"#45454A",fontFamily:"'DM Mono',monospace",padding:"2px 0"}}>{d}</div>)}
          </div>
          {/* Day cells */}
          <div style={{display:"grid",gridTemplateColumns:"repeat(7,1fr)",gap:2}}>
            {calDays.map((d,i)=>(
              <button key={i} disabled={!d||isPast(d)} onClick={()=>{if(d&&!isPast(d)){setSelDate(d);setShowCal(false);}}}
                style={{background:d&&selDate===d?"#C9935A":d&&isToday(d)?"#C9935A22":"none",border:`1px solid ${d&&selDate===d?"#C9935A":d&&isToday(d)?"#C9935A44":"transparent"}`,borderRadius:5,padding:"5px 0",fontSize:11,color:!d?"transparent":isPast(d)?"#2A2A2E":selDate===d?"#0F0F11":isToday(d)?"#C9935A":"#8A8885",cursor:d&&!isPast(d)?"pointer":"default",fontFamily:"'DM Mono',monospace",fontWeight:selDate===d?800:400}}>
                {d||""}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Time picker */}
      {selDate&&(
        <div style={{display:"flex",gap:6,alignItems:"center",marginBottom:10}}>
          <div style={{fontSize:9,color:"#45454A",fontFamily:"'DM Mono',monospace",marginRight:4}}>TIME</div>
          <select value={hour} onChange={e=>setHour(e.target.value)}
            style={{background:"#1C1C1F",border:"1px solid #2A2A2E",borderRadius:5,padding:"5px 8px",color:"#C4C2BD",fontSize:12,fontFamily:"'DM Mono',monospace",outline:"none"}}>
            {Array.from({length:12},(_,i)=>i+1).map(h=><option key={h} value={h}>{h}</option>)}
          </select>
          <span style={{color:"#45454A"}}>:</span>
          <select value={minute} onChange={e=>setMinute(e.target.value)}
            style={{background:"#1C1C1F",border:"1px solid #2A2A2E",borderRadius:5,padding:"5px 8px",color:"#C4C2BD",fontSize:12,fontFamily:"'DM Mono',monospace",outline:"none"}}>
            {["00","15","30","45"].map(m=><option key={m} value={m}>{m}</option>)}
          </select>
          <select value={ampm} onChange={e=>setAmpm(e.target.value)}
            style={{background:"#1C1C1F",border:"1px solid #2A2A2E",borderRadius:5,padding:"5px 8px",color:"#C4C2BD",fontSize:12,fontFamily:"'DM Mono',monospace",outline:"none"}}>
            <option>AM</option><option>PM</option>
          </select>
        </div>
      )}

      {/* Note + Add */}
      <div style={{display:"flex",gap:8}}>
        <input value={note} onChange={e=>setNote(e.target.value)} placeholder="Reminder note..."
          onKeyDown={e=>e.key==="Enter"&&handleAdd()}
          style={{flex:1,background:"#1C1C1F",border:"1px solid #2A2A2E",borderRadius:6,padding:"7px 10px",color:"#C4C2BD",fontSize:12,outline:"none"}}/>
        <button onClick={handleAdd} disabled={!selDate}
          style={{background:selDate?"#C9935A":"#1C1C1F",border:"none",borderRadius:6,color:selDate?"#0F0F11":"#2A2A2E",padding:"7px 14px",cursor:selDate?"pointer":"not-allowed",fontSize:11,fontWeight:800,fontFamily:"'DM Mono',monospace",whiteSpace:"nowrap"}}>+ ADD</button>
      </div>
    </div>
  );
}

// ─── Lead Modal ───────────────────────────────────────────────────────────────

// ─── Photos Tab ───────────────────────────────────────────────────────────────
function PhotosTab({lead, onUpdate}) {
  const [uploading, setUploading] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const fileInputRef = useRef(null);
  const photos = lead.photos || [];

  const uploadPhotos = async(files) => {
    if(!files.length) return;
    setUploading(true);
    const newPhotos = [];
    for(const file of Array.from(files).slice(0, 10)) {
      const ext = file.name.split('.').pop() || 'jpg';
      const path = `${lead.id}/${Date.now()}_${Math.random().toString(36).slice(2)}.${ext}`;
      const mimeType = file.type || 'image/jpeg';
      const b64 = await new Promise((res,rej)=>{const r=new FileReader();r.onload=()=>res(r.result.split(',')[1]);r.onerror=rej;r.readAsDataURL(file);});
      const { data, error } = await supabase.storage
        .from('property-photos')
        .upload(path, file, { upsert: false });
      if(!error) {
        const { data: urlData } = supabase.storage.from('property-photos').getPublicUrl(path);
        if(urlData?.publicUrl) newPhotos.push({url:urlData.publicUrl, data:b64, mimeType});
      }
    }
    if(newPhotos.length > 0) {
      const updated = { ...lead, photos: [...photos, ...newPhotos] };
      onUpdate(updated);
    }
    setUploading(false);
  };

  const deletePhoto = async(photo, idx) => {
    setDeleting(idx);
    const url = typeof photo === 'object' ? photo.url : photo;
    const path = url.split('/property-photos/')[1];
    if(path) await supabase.storage.from('property-photos').remove([path]);
    const updated = { ...lead, photos: photos.filter((_, i) => i !== idx) };
    onUpdate(updated);
    setDeleting(null);
  };

  return (
    <div style={{flex:1, overflowY:'auto', padding:20}}>
      <input ref={fileInputRef} type="file" accept="image/*" multiple style={{display:'none'}}
        onChange={e => uploadPhotos(e.target.files)}/>
      <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16}}>
        <div style={{fontSize:9, letterSpacing:3, color:'#45454A', fontFamily:"'DM Mono',monospace"}}>PROPERTY PHOTOS</div>
        <button onClick={() => fileInputRef.current?.click()} disabled={uploading}
          style={{background:'#C9A96E14', border:'1px solid #C9A96E33', borderRadius:7, color:'#C9A96E', padding:'7px 14px', fontSize:11, cursor:'pointer', fontFamily:"'DM Mono',monospace", fontWeight:700, opacity:uploading?0.6:1}}>
          {uploading ? 'UPLOADING...' : '+ ADD PHOTOS'}
        </button>
      </div>
      {photos.length === 0 ? (
        <div style={{background:'#111113', border:'1px dashed #2A2A2E', borderRadius:10, padding:48, textAlign:'center', cursor:'pointer'}} onClick={() => fileInputRef.current?.click()}>
          <div style={{fontSize:36, marginBottom:12}}>📷</div>
          <div style={{fontSize:13, fontWeight:600, color:'#C4C2BD', marginBottom:6}}>Upload property photos</div>
          <div style={{fontSize:11, color:'#6B6B70'}}>Interior, exterior, damage — stored permanently in this lead</div>
          <div style={{fontSize:10, color:'#45454A', marginTop:8}}>AI Condition analysis will automatically use these photos</div>
        </div>
      ) : (
        <div style={{display:'grid', gridTemplateColumns:'repeat(3, 1fr)', gap:10}}>
          {photos.map((url, i) => (
            <div key={i} style={{position:'relative', aspectRatio:'4/3', borderRadius:8, overflow:'hidden', border:'1px solid #1C1C1F', background:'#111113'}}>
              <img src={typeof url==='object'?url.url:url} style={{width:'100%', height:'100%', objectFit:'cover', display:'block'}}/>
              <button onClick={() => deletePhoto(url, i)} disabled={deleting===i}
                style={{position:'absolute', top:6, right:6, background:'#000A', border:'none', borderRadius:'50%', width:24, height:24, cursor:'pointer', color:'#fff', fontSize:12, display:'flex', alignItems:'center', justifyContent:'center', lineHeight:1}}>
                {deleting===i ? '…' : '✕'}
              </button>
            </div>
          ))}
          <div style={{aspectRatio:'4/3', borderRadius:8, border:'1px dashed #2A2A2E', background:'#111113', display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer', flexDirection:'column', gap:6}} onClick={() => fileInputRef.current?.click()}>
            <div style={{fontSize:24, color:'#45454A'}}>+</div>
            <div style={{fontSize:10, color:'#45454A'}}>Add more</div>
          </div>
        </div>
      )}
      {photos.length > 0 && (
        <div style={{marginTop:12, padding:'8px 12px', background:'#141416', borderRadius:7, fontSize:10, color:'#5A8A60'}}>
          ✓ {photos.length} photo{photos.length > 1 ? 's' : ''} stored — AI Condition will use these automatically
        </div>
      )}
    </div>
  );
}

function LeadModal({lead,rehabConfig,maoFormula,onClose,onUpdate,markets=[]}) {
  const [aiOpen,setAiOpen]=useState(false);
  const [smsInput,setSmsInput]=useState("");
  const [activeTab,setActiveTab]=useState("details");
  const [streetViewImg,setStreetViewImg]=useState(null);
  const [svLoading,setSvLoading]=useState(false);
  const [showFeePrompt,setShowFeePrompt]=useState(false);
  const [feeInput,setFeeInput]=useState("");
  const [fuDate,setFuDate]=useState("");
  const [fuNote,setFuNote]=useState("");
  const stage=stageOf(lead.stage);
  const rehabCost=calcRehab(lead.rehabTier,lead.sqft||1400,rehabConfig,lead.market);

  useEffect(()=>{
    if(!lead.address||lead.address==="Unknown")return;
    setSvLoading(true);
    const url=`https://maps.googleapis.com/maps/api/streetview?size=800x400&location=${encodeURIComponent(lead.address)}&key=${GOOGLE_SV_KEY}`;
    fetch(url).then(r=>r.blob()).then(blob=>new Promise(res=>{const reader=new FileReader();reader.onload=()=>res(reader.result.split(",")[1]);reader.readAsDataURL(blob);})).then(b64=>{setStreetViewImg(b64);setSvLoading(false);}).catch(()=>setSvLoading(false));
  },[lead.address]);

  const sendSMS=()=>{if(!smsInput.trim())return;onUpdate({...lead,smsHistory:[...lead.smsHistory,{from:"out",text:smsInput.trim(),time:"Just now"}],lastContact:"Just now"});setSmsInput("");};

  return (
    <>
      <div style={{position:"fixed",inset:0,background:"#000B",zIndex:50,display:"flex",alignItems:"center",justifyContent:"center"}} onClick={e=>e.target===e.currentTarget&&onClose()}>
        <div style={{background:"#111113",border:"1px solid #1C1C1F",borderRadius:14,width:860,maxHeight:"92vh",overflow:"hidden",display:"flex",flexDirection:"column",boxShadow:"0 30px 80px #0009"}}>
          <div style={{padding:"20px 24px 16px",borderBottom:"1px solid #1C1C1F",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
            <div>
              <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:6}}>
                <div style={{width:8,height:8,borderRadius:"50%",background:stage.color}}/>
                <span style={{fontSize:10,fontFamily:"'DM Mono',monospace",color:stage.color,letterSpacing:2}}>{stage.label.toUpperCase()}</span>
                {lead.hot&&<span style={{fontSize:10,fontFamily:"'DM Mono',monospace",background:"#C06149",color:"#0F0F11",borderRadius:4,padding:"2px 8px",fontWeight:800}}>🔥 HOT</span>}
              </div>
              <div style={{fontSize:22,fontWeight:900,color:"#E8E6E1"}}>{lead.name}</div>
              <div style={{fontSize:12,color:"#6B6B70",marginTop:2}}>{lead.phone} · {lead.address}</div>
            </div>
            <div style={{display:"flex",gap:8}}>
              <button onClick={()=>setAiOpen(true)} style={{background:"#C9A96E14",border:"1px solid #C9A96E33",borderRadius:8,color:"#C9A96E",padding:"8px 16px",fontSize:11,cursor:"pointer",fontFamily:"'DM Mono',monospace",fontWeight:700}}>⚡ AI AGENT</button>
              <button onClick={onClose} style={{background:"none",border:"none",color:"#6B6B70",cursor:"pointer",fontSize:22}}>✕</button>
            </div>
          </div>
          <div style={{display:"flex",borderBottom:"1px solid #1C1C1F",padding:"0 24px"}}>
            {[["details","Details"],["comps",lead.propertyType==="Land"?"Comps & FMV":"Comps & ARV"],["photos","Photos"],["sms","SMS"]].map(([id,label])=>(
              <button key={id} onClick={()=>setActiveTab(id)} style={{background:"none",border:"none",padding:"10px 16px",fontSize:12,cursor:"pointer",fontFamily:"'DM Mono',monospace",color:activeTab===id?"#C9A96E":"#45454A",borderBottom:activeTab===id?"2px solid #C9A96E":"2px solid transparent"}}>{label}</button>
            ))}
          </div>
          <div style={{flex:1,overflow:"hidden",display:"flex"}}>
            {activeTab==="details"&&(
              <div style={{flex:1,overflowY:"auto",padding:20}}>
                <div style={{marginBottom:20}}>
                  <div style={{fontSize:9,letterSpacing:3,color:"#45454A",fontFamily:"'DM Mono',monospace",marginBottom:8}}>PROPERTY · STREET VIEW</div>
                  <div style={{borderRadius:10,overflow:"hidden",border:"1px solid #1C1C1F",background:"#111113"}}>
                    {svLoading&&<div style={{height:180,display:"flex",alignItems:"center",justifyContent:"center",gap:10,color:"#45454A",fontSize:12}}><div style={{width:8,height:8,borderRadius:"50%",background:"#C9A96E",animation:"aiPulse 1s infinite"}}/>Loading street view...</div>}
                    {!svLoading&&streetViewImg&&<img src={`data:image/jpeg;base64,${streetViewImg}`} alt="Street View" style={{width:"100%",display:"block",maxHeight:200,objectFit:"cover"}}/>}
                    {!svLoading&&!streetViewImg&&<div style={{height:160,display:"flex",alignItems:"center",justifyContent:"center",flexDirection:"column",gap:8,color:"#2A2A2E"}}><div style={{fontSize:28}}>🏠</div><div style={{fontSize:11,fontFamily:"'DM Mono',monospace"}}>No street view available</div></div>}
                    <div style={{padding:"8px 12px",background:"#111113",display:"flex",justifyContent:"space-between"}}>
                      <div style={{fontSize:10,color:"#45454A",fontFamily:"'DM Mono',monospace"}}>📍 {lead.address}</div>
                      {lead.conditionReport&&<div style={{fontSize:10,color:"#5A8A60",fontFamily:"'DM Mono',monospace"}}>✓ Analyzed</div>}
                    </div>
                  </div>
                </div>
                <div style={{marginBottom:20}}>
                  <div style={{fontSize:9,letterSpacing:3,color:"#45454A",fontFamily:"'DM Mono',monospace",marginBottom:8}}>MOVE STAGE</div>
                  <div style={{display:"flex",flexWrap:"wrap",gap:6}}>
                    {STAGES.map(s=>(
                      <button key={s.id} onClick={()=>{
                        if(s.id==="contract"&&lead.stage!=="contract"){setShowFeePrompt(true);setFeeInput("");onUpdate({...lead,stage:s.id});}
                        else{setShowFeePrompt(false);onUpdate({...lead,stage:s.id});}
                      }} style={{padding:"5px 10px",borderRadius:5,cursor:"pointer",fontSize:10,fontFamily:"'DM Mono',monospace",background:lead.stage===s.id?s.color+"22":"none",border:`1px solid ${lead.stage===s.id?s.color:"#2A2A2E"}`,color:lead.stage===s.id?s.color:"#45454A"}}>{s.icon} {s.label}</button>
                    ))}
                  </div>
                  {showFeePrompt&&(
                    <div style={{marginTop:12,background:"#B87AA80D",border:"1px solid #B87AA833",borderRadius:10,padding:14}}>
                      <div style={{fontSize:10,color:"#B87AA8",fontFamily:"'DM Mono',monospace",letterSpacing:2,marginBottom:8}}>★ EST. WHOLESALE FEE</div>
                      <div style={{display:"flex",gap:8,alignItems:"center"}}>
                        <span style={{fontSize:13,color:"#45454A",fontFamily:"'DM Mono',monospace"}}>$</span>
                        <input autoFocus type="number" min="0" step="500" value={feeInput}
                          onChange={e=>setFeeInput(e.target.value)}
                          onKeyDown={e=>{if(e.key==="Enter"&&feeInput){onUpdate({...lead,stage:"contract",wholesaleFee:parseInt(feeInput)||null});setShowFeePrompt(false);}if(e.key==="Escape")setShowFeePrompt(false);}}
                          placeholder="e.g. 10000"
                          style={{flex:1,background:"#1C1C1F",border:"1px solid #B87AA833",borderRadius:6,padding:"8px 10px",color:"#B87AA8",fontSize:16,fontFamily:"'DM Mono',monospace",fontWeight:800,outline:"none",boxSizing:"border-box"}}/>
                        <button onClick={()=>{onUpdate({...lead,stage:"contract",wholesaleFee:parseInt(feeInput)||null});setShowFeePrompt(false);}}
                          style={{background:"#B87AA8",border:"none",borderRadius:6,color:"#0F0F11",padding:"8px 16px",cursor:"pointer",fontSize:11,fontWeight:800,fontFamily:"'DM Mono',monospace",whiteSpace:"nowrap"}}>SAVE ↗</button>
                        <button onClick={()=>setShowFeePrompt(false)}
                          style={{background:"none",border:"1px solid #2A2A2E",borderRadius:6,color:"#45454A",padding:"8px 12px",cursor:"pointer",fontSize:11}}>Skip</button>
                      </div>
                      {feeInput&&<div style={{fontSize:10,color:"#45454A",fontFamily:"'DM Mono',monospace",marginTop:8}}>
                        Fee: <span style={{color:"#B87AA8"}}>{fmt$(parseInt(feeInput)||0)}</span>
                        {lead.arv&&<> · {(((parseInt(feeInput)||0)/lead.arv.value)*100).toFixed(1)}% of {lead.propertyType==="Land"?"FMV":"ARV"}</>}
                      </div>}
                    </div>
                  )}
                </div>
                <div style={{marginBottom:20}}>
                  <div style={{fontSize:9,letterSpacing:3,color:"#45454A",fontFamily:"'DM Mono',monospace",marginBottom:8}}>PROPERTY TYPE</div>
                  <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                    {["Single Family","Land","Multi-Family","Commercial"].map(pt=>(
                      <button key={pt} onClick={()=>onUpdate({...lead,propertyType:pt})}
                        style={{padding:"5px 12px",borderRadius:5,cursor:"pointer",fontSize:10,
                        fontFamily:"'DM Mono',monospace",
                        background:lead.propertyType===pt?"#C9A96E22":"none",
                        border:"1px solid "+(lead.propertyType===pt?"#C9A96E":"#2A2A2E"),
                        color:lead.propertyType===pt?"#C9A96E":"#45454A"}}>
                        {pt==="Single Family"?"🏠":pt==="Land"?"🌿":pt==="Multi-Family"?"🏢":"🏪"} {pt}
                      </button>
                    ))}
                  </div>
                </div>
                {markets.length>0&&(
                  <div style={{marginBottom:20}}>
                    <div style={{fontSize:9,letterSpacing:3,color:"#45454A",fontFamily:"'DM Mono',monospace",marginBottom:8}}>MARKET</div>
                    <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                      {markets.map(m=>(<button key={m} onClick={()=>onUpdate({...lead,market:m})} style={{padding:"5px 12px",borderRadius:5,cursor:"pointer",fontSize:10,fontFamily:"'DM Mono',monospace",background:lead.market===m?"#C9935A22":"none",border:`1px solid ${lead.market===m?"#C9935A":"#2A2A2E"}`,color:lead.market===m?"#C9935A":"#45454A"}}>{m}</button>))}
                      {lead.market&&<button onClick={()=>onUpdate({...lead,market:""})} style={{padding:"5px 10px",borderRadius:5,cursor:"pointer",fontSize:10,fontFamily:"'DM Mono',monospace",background:"none",border:"1px dashed #2A2A2E",color:"#45454A"}}>✕ Clear</button>}
                    </div>
                  </div>
                )}
                <div style={{marginBottom:20}}>
                  <div style={{fontSize:9,letterSpacing:3,color:"#45454A",fontFamily:"'DM Mono',monospace",marginBottom:8}}>DISTRESS SIGNALS <span style={{color:"#6B6B70",fontWeight:400,fontSize:9,letterSpacing:0}}>— click to edit</span></div>
                  <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr 1fr",gap:8}}>
                    <div style={{background:"#111113",border:`1px solid ${lead.equity>50?"#C9A96E33":"#1C1C1F"}`,borderRadius:7,padding:"10px 12px"}}>
                      <div style={{fontSize:9,color:"#45454A",fontFamily:"'DM Mono',monospace",marginBottom:4}}>EQUITY %</div>
                      <input type="number" value={lead.equity||0} onChange={e=>onUpdate({...lead,equity:parseInt(e.target.value)||0})} style={{width:"100%",background:"none",border:"none",fontSize:16,fontWeight:800,color:lead.equity>50?"#C9A96E":"#C4C2BD",fontFamily:"'DM Mono',monospace",outline:"none",padding:0,cursor:"text"}}/>
                    </div>
                    <div style={{background:"#111113",border:`1px solid ${lead.taxLien?"#C0614933":"#1C1C1F"}`,borderRadius:7,padding:"10px 12px",cursor:"pointer"}} onClick={()=>onUpdate({...lead,taxLien:!lead.taxLien})}>
                      <div style={{fontSize:9,color:"#45454A",fontFamily:"'DM Mono',monospace",marginBottom:4}}>TAX LIEN</div>
                      <div style={{fontSize:16,fontWeight:800,color:lead.taxLien?"#C06149":"#C4C2BD"}}>{lead.taxLien?"YES":"NO"}</div>
                    </div>
                    <div style={{background:"#111113",border:`1px solid ${lead.vacant?"#C9935A33":"#1C1C1F"}`,borderRadius:7,padding:"10px 12px",cursor:"pointer"}} onClick={()=>onUpdate({...lead,vacant:!lead.vacant})}>
                      <div style={{fontSize:9,color:"#45454A",fontFamily:"'DM Mono',monospace",marginBottom:4}}>VACANT</div>
                      <div style={{fontSize:16,fontWeight:800,color:lead.vacant?"#C9935A":"#C4C2BD"}}>{lead.vacant?"YES":"NO"}</div>
                    </div>
                    <div style={{background:"#111113",border:"1px solid #1C1C1F",borderRadius:7,padding:"10px 12px"}}>
                      <div style={{fontSize:9,color:"#45454A",fontFamily:"'DM Mono',monospace",marginBottom:4}}>SQFT</div>
                      <input type="number" value={lead.sqft||""} onChange={e=>onUpdate({...lead,sqft:parseInt(e.target.value)||null})} placeholder="?" style={{width:"100%",background:"none",border:"none",fontSize:16,fontWeight:800,color:"#C4C2BD",fontFamily:"'DM Mono',monospace",outline:"none",padding:0,cursor:"text"}}/>
                    </div>
                  </div>
                  {(lead.propertyType==="Single Family"||lead.propertyType==="Multi-Family")&&(
                    <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginTop:8}}>
                      <div style={{background:"#111113",border:"1px solid #1C1C1F",borderRadius:7,padding:"10px 12px"}}>
                        <div style={{fontSize:9,color:"#45454A",fontFamily:"'DM Mono',monospace",marginBottom:4}}>BEDS</div>
                        <input type="number" value={lead.bedrooms||""} onChange={e=>onUpdate({...lead,bedrooms:parseInt(e.target.value)||null})} placeholder="?" style={{width:"100%",background:"none",border:"none",fontSize:16,fontWeight:800,color:"#C4C2BD",fontFamily:"'DM Mono',monospace",outline:"none",padding:0,cursor:"text"}}/>
                      </div>
                      <div style={{background:"#111113",border:"1px solid #1C1C1F",borderRadius:7,padding:"10px 12px"}}>
                        <div style={{fontSize:9,color:"#45454A",fontFamily:"'DM Mono',monospace",marginBottom:4}}>BATHS</div>
                        <input type="number" step="0.5" value={lead.bathrooms||""} onChange={e=>onUpdate({...lead,bathrooms:parseFloat(e.target.value)||null})} placeholder="?" style={{width:"100%",background:"none",border:"none",fontSize:16,fontWeight:800,color:"#C4C2BD",fontFamily:"'DM Mono',monospace",outline:"none",padding:0,cursor:"text"}}/>
                      </div>
                    </div>
                  )}
                </div>
                <div style={{marginBottom:20}}>
                  {lead.propertyType==="Land"?(
                    <div>
                      <div style={{fontSize:9,letterSpacing:3,color:"#45454A",fontFamily:"'DM Mono',monospace",marginBottom:8}}>LAND OFFER FORMULA</div>
                      <div style={{background:"#111113",border:"1px solid #5A8A6033",borderRadius:8,padding:14}}>
                        <div style={{fontSize:9,color:"#45454A",fontFamily:"'DM Mono',monospace",marginBottom:4}}>FMV MULTIPLIER</div>
                        <div style={{fontSize:22,fontWeight:800,color:"#5A8A60",fontFamily:"'DM Mono',monospace"}}>{(maoFormula?.land?.multiplier||50)}%</div>
                        <div style={{fontSize:10,color:"#45454A",fontFamily:"'DM Mono',monospace",marginTop:6}}>MAO = FMV × {maoFormula?.land?.multiplier||50}%{(maoFormula?.land?.assignmentFee||0)>0&&<> − <span style={{color:"#B87AA8"}}>${(maoFormula?.land?.assignmentFee||0).toLocaleString()} fee</span></>} — set in ⚙ Cost Configuration</div>
                      </div>
                    </div>
                  ):(
                    <div>
                      <div style={{fontSize:9,letterSpacing:3,color:"#45454A",fontFamily:"'DM Mono',monospace",marginBottom:8}}>REHAB ESTIMATE</div>
                      <div style={{display:"flex",gap:8}}>
                        {["light","medium","heavy"].map(t=>{
                          const defaultTiers=rehabConfig.__default__||rehabConfig;
                          const cfg=(rehabConfig[lead.market]?.[t])||defaultTiers[t]||DEFAULT_REHAB[t];
                          const cost=calcRehab(t,lead.sqft||1400,rehabConfig,lead.market);
                          const active=lead.rehabTier===t;return(
                          <button key={t} onClick={()=>onUpdate({...lead,rehabTier:t})} style={{flex:1,padding:"10px 8px",borderRadius:8,cursor:"pointer",textAlign:"center",background:active?cfg.color+"18":"#111113",border:`1px solid ${active?cfg.color:"#1C1C1F"}`}}>
                            <div style={{fontSize:9,fontFamily:"'DM Mono',monospace",color:active?cfg.color:"#45454A",marginBottom:4}}>{cfg.label.split(" ")[0].toUpperCase()}</div>
                            <div style={{fontSize:14,fontWeight:800,color:active?cfg.color:"#6B6B70",fontFamily:"'DM Mono',monospace"}}>{fmt$(cost)}</div>
                          </button>
                        );})}
                      </div>
                    </div>
                  )}
                </div>
                {lead.stage==="contract"&&lead.wholesaleFee&&!lead.arv&&(
                  <div style={{marginBottom:20}}>
                    <div style={{fontSize:9,letterSpacing:3,color:"#45454A",fontFamily:"'DM Mono',monospace",marginBottom:8}}>WHOLESALE FEE</div>
                    <div style={{background:"#B87AA80D",border:"1px solid #B87AA833",borderRadius:8,padding:14,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                      <div style={{fontSize:9,color:"#B87AA8",fontFamily:"'DM Mono',monospace",letterSpacing:2}}>★ EST. WHOLESALE FEE</div>
                      <div style={{fontSize:22,fontWeight:800,color:"#B87AA8",fontFamily:"'DM Mono',monospace"}}>{fmt$(lead.wholesaleFee)}</div>
                    </div>
                  </div>
                )}
                {lead.arv&&(
                  <div style={{marginBottom:20}}>
                    <div style={{fontSize:9,letterSpacing:3,color:"#45454A",fontFamily:"'DM Mono',monospace",marginBottom:8}}>DEAL NUMBERS</div>
                    <div style={{background:"#111113",border:"1px solid #1C1C1F",borderRadius:8,padding:14}}>
                      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:10,marginBottom:lead.stage==="contract"?12:0}}>
                        {lead.propertyType==="Land"
                          ?[["FMV",fmt$(lead.arv.value),"#C9A96E"],["MAO",fmt$(calcMAO(lead,rehabCost,maoFormula)),"#5A8A60"]].map(([k,v,c])=>(
                            <div key={k}><div style={{fontSize:9,color:"#45454A",fontFamily:"'DM Mono',monospace"}}>{k}</div><div style={{fontSize:16,fontWeight:800,color:c,fontFamily:"'DM Mono',monospace"}}>{v}</div></div>
                          ))
                          :[
                            <div key="arv">
                              <div style={{fontSize:9,color:"#45454A",fontFamily:"'DM Mono',monospace",marginBottom:2}}>ARV {lead.arv?.manual&&<span style={{color:"#6B6B70",fontSize:8}}>MANUAL</span>}</div>
                              <input type="number" value={lead.arv?.value||""} onChange={e=>onUpdate({...lead,arv:{...lead.arv,value:parseInt(e.target.value)||0,manual:true}})} style={{background:"none",border:"none",borderBottom:"1px solid #C9A96E44",fontSize:16,fontWeight:800,color:"#C9A96E",fontFamily:"'DM Mono',monospace",outline:"none",padding:0,width:"100%",cursor:"text"}}/>
                            </div>,
                            <div key="rep"><div style={{fontSize:9,color:"#45454A",fontFamily:"'DM Mono',monospace"}}>{"REPAIRS"}</div><div style={{fontSize:16,fontWeight:800,color:"#C9935A",fontFamily:"'DM Mono',monospace"}}>{lead.rehabTier?fmt$(rehabCost):"Set tier"}</div></div>,
                            <div key="mao"><div style={{fontSize:9,color:"#45454A",fontFamily:"'DM Mono',monospace"}}>MAO</div><div style={{fontSize:16,fontWeight:800,color:"#5A8A60",fontFamily:"'DM Mono',monospace"}}>{fmt$(calcMAO(lead,rehabCost,maoFormula))}</div></div>
                          ]
                        }
                      </div>
                      {lead.stage==="contract"&&(
                        <div style={{borderTop:"1px solid #1C1C1F",paddingTop:12}}>
                          <div style={{fontSize:9,color:"#B87AA8",fontFamily:"'DM Mono',monospace",marginBottom:6,letterSpacing:2}}>★ EST. WHOLESALE FEE</div>
                          <div style={{display:"flex",gap:8,alignItems:"center"}}>
                            <input type="number" min="0" step="500"
                              value={lead.wholesaleFee||""}
                              onChange={e=>onUpdate({...lead,wholesaleFee:parseInt(e.target.value)||null})}
                              placeholder="e.g. 10000"
                              style={{flex:1,background:"#1C1C1F",border:"1px solid #B87AA833",borderRadius:6,padding:"8px 10px",color:"#B87AA8",fontSize:16,fontFamily:"'DM Mono',monospace",fontWeight:800,outline:"none",boxSizing:"border-box"}}/>
                            {lead.wholesaleFee&&<div style={{fontSize:13,fontWeight:800,color:"#B87AA8",fontFamily:"'DM Mono',monospace",whiteSpace:"nowrap"}}>{fmt$(lead.wholesaleFee)}</div>}
                          </div>
                          {lead.wholesaleFee&&lead.arv&&(
                            <div style={{fontSize:10,color:"#45454A",fontFamily:"'DM Mono',monospace",marginTop:6}}>
                              {((lead.wholesaleFee/lead.arv.value)*100).toFixed(1)}% of {lead.propertyType==="Land"?"FMV":"ARV"} · Net to seller: <span style={{color:"#5A8A60"}}>{fmt$(calcMAO(lead,rehabCost,maoFormula)-lead.wholesaleFee)}</span>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                )}
                <div><div style={{fontSize:9,letterSpacing:3,color:"#45454A",fontFamily:"'DM Mono',monospace",marginBottom:8}}>NOTES</div><div style={{background:"#111113",border:"1px solid #1C1C1F",borderRadius:8,padding:12,fontSize:13,color:"#8A8885",lineHeight:1.6}}>{lead.notes||"No notes yet."}</div></div>

                {/* ── Follow-ups ── */}
                <div style={{marginTop:20}}>
                  <div style={{fontSize:9,letterSpacing:3,color:"#45454A",fontFamily:"'DM Mono',monospace",marginBottom:8}}>FOLLOW-UPS</div>
                  {(lead.followUps||[]).length>0&&(
                    <div style={{marginBottom:10}}>
                      {(lead.followUps||[]).map((fu,i)=>(
                        <div key={i} style={{background:"#111113",border:`1px solid ${fu.done?"#1C1C1F":"#C9935A33"}`,borderRadius:8,padding:"10px 12px",marginBottom:6,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                          <div style={{display:"flex",alignItems:"center",gap:10}}>
                            <input type="checkbox" checked={!!fu.done} onChange={()=>{
                              const updated=[...(lead.followUps||[])];
                              updated[i]={...updated[i],done:!fu.done};
                              onUpdate({...lead,followUps:updated});
                            }} style={{width:14,height:14,accentColor:"#5A8A60",cursor:"pointer"}}/>
                            <div>
                              <div style={{fontSize:12,color:fu.done?"#45454A":"#C4C2BD",textDecoration:fu.done?"line-through":"none",fontWeight:600}}>{fu.note||"Follow up"}</div>
                              <div style={{fontSize:10,color:"#C9935A",fontFamily:"'DM Mono',monospace",marginTop:2}}>⏰ {fu.due}</div>
                            </div>
                          </div>
                          <button onClick={()=>{const updated=(lead.followUps||[]).filter((_,idx)=>idx!==i);onUpdate({...lead,followUps:updated});}}
                            style={{background:"none",border:"none",color:"#45454A",cursor:"pointer",fontSize:16,padding:"0 4px"}}>×</button>
                        </div>
                      ))}
                    </div>
                  )}
                  <FollowUpPicker onAdd={(fu)=>onUpdate({...lead,followUps:[...(lead.followUps||[]),fu]})}/>
                </div>
              </div>
            )}
            {activeTab==="photos"&&<PhotosTab lead={lead} onUpdate={onUpdate}/>}
            {activeTab==="comps"&&<div style={{flex:1,overflowY:"auto",padding:20}}><CompsPanel lead={lead} onUpdate={onUpdate}/></div>}
            {activeTab==="sms"&&(
              <div style={{flex:1,display:"flex",flexDirection:"column"}}>
                <div style={{flex:1,overflowY:"auto",padding:16}}>
                  {lead.smsHistory.length===0&&<div style={{textAlign:"center",color:"#2A2A2E",fontSize:12,padding:"40px 0"}}>No messages yet</div>}
                  {lead.smsHistory.map((m,i)=><Bubble key={i} msg={m}/>)}
                </div>
                <div style={{padding:"12px 14px",borderTop:"1px solid #1C1C1F",display:"flex",gap:8}}>
                  <input value={smsInput} onChange={e=>setSmsInput(e.target.value)} onKeyDown={e=>e.key==="Enter"&&sendSMS()} placeholder="Type message or use AI Agent to draft..." style={{flex:1,background:"#1C1C1F",border:"1px solid #2A2A2E",borderRadius:8,padding:"10px 12px",color:"#E8E6E1",fontSize:12,outline:"none"}}/>
                  <button onClick={sendSMS} style={{background:"#C9A96E",border:"none",borderRadius:8,color:"#0F0F11",padding:"10px 16px",cursor:"pointer",fontWeight:800,fontSize:12}}>SEND</button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      {aiOpen&&<AIPanel lead={lead} rehabConfig={rehabConfig} onClose={()=>setAiOpen(false)} onUpdate={onUpdate} storedPhotos={lead.photos||[]}/>}
    </>
  );
}

// ─── New Lead Modal ───────────────────────────────────────────────────────────
function NewLeadModal({onClose,onAdd,markets=[]}) {
  const [form,setForm]=useState({name:"",phone:"",address:"",sqft:"",bedrooms:"",bathrooms:"",equity:"",taxLien:false,vacant:false,notes:"",stage:"new",market:"",propertyType:"Single Family"});
  const [error,setError]=useState("");
  const [lookupStatus,setLookupStatus]=useState(null);
  const set=(k,v)=>setForm(f=>({...f,[k]:v}));
  const isLand=form.propertyType==="Land";

  const lookupAddress=async(address)=>{
    if(!RENTCAST_KEY||!address.trim())return;
    const parts=address.trim().split(",");
    if(parts.length<2)return;
    setLookupStatus("loading");
    try{
      const params=new URLSearchParams({address:address.trim(),limit:"1"});
      const res=await fetch("https://api.rentcast.io/v1/properties?"+params,{
        headers:{"X-Api-Key":RENTCAST_KEY,"Content-Type":"application/json"}});
      if(!res.ok){setLookupStatus("notfound");return;}
      const data=await res.json();
      const prop=Array.isArray(data)?data[0]:data;
      if(prop){
        setForm(f=>({...f,
          sqft:prop.squareFootage?String(prop.squareFootage):f.sqft,
          bedrooms:prop.bedrooms?String(prop.bedrooms):f.bedrooms,
          bathrooms:prop.bathrooms?String(prop.bathrooms):f.bathrooms,
        }));
        setLookupStatus("found");
      }else{setLookupStatus("notfound");}
    }catch(e){setLookupStatus("notfound");}
  };
  const handleAdd=()=>{
    if(!form.name.trim()){setError("Name is required.");return;}
    if(!form.address.trim()){setError("Address is required.");return;}
    onAdd({id:Date.now(),stage:form.stage,name:form.name.trim(),phone:form.phone.trim(),address:form.address.trim(),sqft:parseInt(form.sqft)||null,bedrooms:parseInt(form.bedrooms)||null,bathrooms:parseFloat(form.bathrooms)||null,equity:parseInt(form.equity)||0,taxLien:form.taxLien,vacant:form.vacant,score:50,hot:false,notes:form.notes.trim(),arv:null,comps:[],rehabTier:null,conditionReport:null,smsHistory:[],followUps:[],lastContact:null,market:form.market.trim(),propertyType:form.propertyType,wholesaleFee:null});
    onClose();
  };
  const fs={width:"100%",boxSizing:"border-box",background:"#0F0F11",border:"1px solid #1C1C1F",borderRadius:8,padding:"10px 12px",color:"#E8E6E1",fontSize:13,fontFamily:"'DM Sans',sans-serif",outline:"none"};
  const ls={display:"block",fontSize:10,fontFamily:"'DM Mono',monospace",color:"#6B6B70",letterSpacing:1,marginBottom:6};
  return (
    <div style={{position:"fixed",inset:0,background:"#000B",zIndex:50,display:"flex",alignItems:"center",justifyContent:"center"}} onClick={e=>e.target===e.currentTarget&&onClose()}>
      <div style={{background:"#111113",border:"1px solid #1C1C1F",borderRadius:14,width:560,maxHeight:"90vh",overflow:"hidden",display:"flex",flexDirection:"column",boxShadow:"0 30px 80px #0009"}}>
        <div style={{padding:"20px 24px 16px",borderBottom:"1px solid #1C1C1F",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
          <div><div style={{fontSize:10,letterSpacing:3,color:"#C9A96E",fontFamily:"'DM Mono',monospace",marginBottom:4}}>NEW LEAD</div><div style={{fontSize:20,fontWeight:800,color:"#E8E6E1"}}>Add Lead to Pipeline</div></div>
          <button onClick={onClose} style={{background:"none",border:"none",color:"#6B6B70",cursor:"pointer",fontSize:22}}>✕</button>
        </div>
        <div style={{overflowY:"auto",padding:24}}>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:16,marginBottom:16}}>
            <div><label style={ls}>SELLER NAME *</label><input value={form.name} onChange={e=>set("name",e.target.value)} placeholder="John Smith" style={fs}/></div>
            <div><label style={ls}>PHONE NUMBER</label><input value={form.phone} onChange={e=>set("phone",e.target.value)} placeholder="(713) 555-1234" style={fs}/></div>
          </div>
          <div style={{marginBottom:16}}>
            <label style={ls}>PROPERTY ADDRESS *</label>
            <div style={{position:"relative"}}>
              <input value={form.address}
                onChange={e=>{set("address",e.target.value);setLookupStatus(null);}}
                onBlur={e=>lookupAddress(e.target.value)}
                placeholder="4821 Oleander Dr, Houston, TX 77018" style={fs}/>
              {lookupStatus==="loading"&&<div style={{position:"absolute",right:10,top:"50%",transform:"translateY(-50%)",fontSize:10,color:"#6B6B70",fontFamily:"'DM Mono',monospace"}}>looking up...</div>}
              {lookupStatus==="found"&&<div style={{position:"absolute",right:10,top:"50%",transform:"translateY(-50%)",fontSize:10,color:"#5A8A60",fontFamily:"'DM Mono',monospace"}}>✓ auto-filled</div>}
              {lookupStatus==="notfound"&&<div style={{position:"absolute",right:10,top:"50%",transform:"translateY(-50%)",fontSize:10,color:"#45454A",fontFamily:"'DM Mono',monospace"}}>not found</div>}
            </div>
          </div>
          <div style={{marginBottom:16}}>
            <label style={ls}>PROPERTY TYPE</label>
            <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
              {[{t:"Single Family",i:"🏠"},{t:"Land",i:"🌿"},{t:"Multi-Family",i:"🏢"},{t:"Commercial",i:"🏪"}].map(({t,i})=>(
                <button key={t} onClick={()=>set("propertyType",t)}
                  style={{padding:"7px 14px",borderRadius:6,cursor:"pointer",fontSize:11,fontFamily:"'DM Mono',monospace",background:form.propertyType===t?"#C9A96E22":"#0F0F11",border:`1px solid ${form.propertyType===t?"#C9A96E":"#1C1C1F"}`,color:form.propertyType===t?"#C9A96E":"#6B6B70"}}>
                  {i} {t}
                </button>
              ))}
            </div>
          </div>
          <div style={{display:"grid",gridTemplateColumns:isLand?"1fr 1fr":"1fr 1fr 1fr",gap:16,marginBottom:16}}>
            {!isLand&&<div><label style={ls}>SQ FT</label><input type="number" value={form.sqft} onChange={e=>set("sqft",e.target.value)} placeholder="1400" style={fs}/></div>}
            <div><label style={ls}>EQUITY %</label><input type="number" value={form.equity} onChange={e=>set("equity",e.target.value)} placeholder="65" style={fs}/></div>
            <div><label style={ls}>STAGE</label><select value={form.stage} onChange={e=>set("stage",e.target.value)} style={{...fs,cursor:"pointer"}}>{STAGES.map(s=><option key={s.id} value={s.id}>{s.label}</option>)}</select></div>
          </div>
          {(form.propertyType==="Single Family"||form.propertyType==="Multi-Family")&&(
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:16,marginBottom:16}}>
              <div><label style={ls}>BEDROOMS</label><input type="number" value={form.bedrooms} onChange={e=>set("bedrooms",e.target.value)} placeholder="3" style={fs}/></div>
              <div><label style={ls}>BATHROOMS</label><input type="number" step="0.5" value={form.bathrooms} onChange={e=>set("bathrooms",e.target.value)} placeholder="2" style={fs}/></div>
            </div>
          )}
          <div style={{marginBottom:16}}>
            <label style={ls}>MARKET</label>
            {markets.length>0
              ? <select value={form.market} onChange={e=>set("market",e.target.value)} style={{...fs,cursor:"pointer"}}>
                  <option value="">— No market —</option>
                  {markets.map(m=><option key={m} value={m}>{m}</option>)}
                </select>
              : <input value={form.market} onChange={e=>set("market",e.target.value)} placeholder="e.g. Houston, Dallas…" style={fs}/>
            }
          </div>
          <div style={{display:"flex",gap:16,marginBottom:16}}>
            <label style={{display:"flex",alignItems:"center",gap:8,cursor:"pointer"}}><input type="checkbox" checked={form.taxLien} onChange={e=>set("taxLien",e.target.checked)} style={{width:16,height:16,accentColor:"#C06149"}}/><span style={{fontSize:13,color:"#C4C2BD"}}>Tax Lien</span></label>
            <label style={{display:"flex",alignItems:"center",gap:8,cursor:"pointer"}}><input type="checkbox" checked={form.vacant} onChange={e=>set("vacant",e.target.checked)} style={{width:16,height:16,accentColor:"#C9935A"}}/><span style={{fontSize:13,color:"#C4C2BD"}}>Vacant</span></label>
          </div>
          <div style={{marginBottom:16}}><label style={ls}>NOTES</label><textarea value={form.notes} onChange={e=>set("notes",e.target.value)} placeholder="Inherited property, motivated seller..." rows={3} style={{...fs,resize:"vertical",lineHeight:1.5}}/></div>
          {error&&<div style={{background:"#C0614911",border:"1px solid #C0614933",borderRadius:8,padding:"10px 14px",marginBottom:16,color:"#C06149",fontSize:13}}>⚠ {error}</div>}
        </div>
        <div style={{padding:"16px 24px",borderTop:"1px solid #1C1C1F",display:"flex",justifyContent:"flex-end",gap:10}}>
          <button onClick={onClose} style={{background:"none",border:"1px solid #2A2A2E",borderRadius:8,color:"#6B6B70",padding:"10px 20px",cursor:"pointer",fontSize:13}}>Cancel</button>
          <button onClick={handleAdd} style={{background:"linear-gradient(135deg,#C9A96E,#A8834A)",border:"none",borderRadius:8,color:"#0F0F11",padding:"10px 24px",cursor:"pointer",fontSize:13,fontWeight:800,fontFamily:"'DM Mono',monospace"}}>+ ADD LEAD</button>
        </div>
      </div>
    </div>
  );
}

// ─── Lead Card ────────────────────────────────────────────────────────────────
function LeadCard({lead,rehabConfig,maoFormula,onClick,onDragStart}) {
  const stage=stageOf(lead.stage);
  const rehabCost=calcRehab(lead.rehabTier,lead.sqft||1400,rehabConfig,lead.market);
  const tierCfg=lead.rehabTier&&(rehabConfig[lead.market]?.[lead.rehabTier]||rehabConfig.__default__?.[lead.rehabTier]||rehabConfig[lead.rehabTier]);
  return (
    <div draggable onDragStart={e=>onDragStart(e,lead.id)} onClick={()=>onClick(lead)}
      style={{background:"#0F0F11",border:`1px solid ${lead.hot?"#C0614933":"#1C1C1F"}`,borderRadius:9,padding:"13px 14px",cursor:"pointer",marginBottom:8,position:"relative"}}
      onMouseEnter={e=>{e.currentTarget.style.borderColor=lead.hot?"#C06149":stage.color+"55";}}
      onMouseLeave={e=>{e.currentTarget.style.borderColor=lead.hot?"#C0614933":"#1C1C1F";}}>
      {lead.hot&&<div style={{position:"absolute",top:8,right:8,background:"#C06149",color:"#0F0F11",fontSize:9,fontWeight:800,fontFamily:"'DM Mono',monospace",borderRadius:4,padding:"2px 6px"}}>🔥 HOT</div>}
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:6}}>
        <div style={{fontSize:13,fontWeight:700,color:"#E8E6E1",paddingRight:lead.hot?44:0}}>{lead.name}</div>
        <div style={{fontSize:12,fontWeight:700,color:lead.score>=80?"#C9A96E":lead.score>=55?"#C9935A":"#45454A",fontFamily:"'DM Mono',monospace"}}>{lead.score}</div>
      </div>
      <div style={{fontSize:10,color:"#45454A",marginBottom:8,lineHeight:1.4}}>{lead.address}</div>
      <div style={{display:"flex",gap:4,flexWrap:"wrap",marginBottom:8}}>
        <Tag label={`EQ ${lead.equity}%`} color="#C9A96E"/>
        {lead.market&&<Tag label={lead.market.toUpperCase()} color="#C9935A"/>}
        {lead.propertyType&&lead.propertyType!=="Single Family"&&<Tag label={lead.propertyType.toUpperCase()} color="#C9A96E"/>}
        {lead.taxLien&&<Tag label="TAX LIEN" color="#C06149"/>}
        {lead.vacant&&<Tag label="VACANT" color="#C9935A"/>}
        {tierCfg&&<Tag label={tierCfg.label.split(" ")[0].toUpperCase()} color={tierCfg.color}/>}
      </div>
      {lead.arv&&(
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:6,marginBottom:8}}>
          {[[lead.propertyType==="Land"?"FMV":"ARV",fmt$(lead.arv.value),"#C9A96E"],...(lead.propertyType==="Land"?[]:[[" REPAIR",lead.rehabTier?fmt$(rehabCost):"—","#C9935A"]]),["MAO",fmt$(calcMAO(lead,rehabCost,maoFormula)),"#5A8A60"]].map(([k,v,c])=>(
            <div key={k} style={{background:"#141416",borderRadius:5,padding:"5px 7px"}}>
              <div style={{fontSize:8,color:"#45454A",fontFamily:"'DM Mono',monospace"}}>{k}</div>
              <div style={{fontSize:11,fontWeight:700,color:c,fontFamily:"'DM Mono',monospace"}}>{v}</div>
            </div>
          ))}
        </div>
      )}
      {lead.followUps?.length>0&&<div style={{fontSize:10,color:"#C9935A"}}>⏰ {lead.followUps[0].due}</div>}
      {lead.stage==="contract"&&lead.wholesaleFee&&(
        <div style={{marginTop:6,background:"#B87AA814",border:"1px solid #B87AA833",borderRadius:6,padding:"5px 8px",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
          <div style={{fontSize:9,color:"#B87AA8",fontFamily:"'DM Mono',monospace",letterSpacing:1}}>★ WHOLESALE FEE</div>
          <div style={{fontSize:13,fontWeight:800,color:"#B87AA8",fontFamily:"'DM Mono',monospace"}}>{fmt$(lead.wholesaleFee)}</div>
        </div>
      )}
    </div>
  );
}


// ─── Upgrade Modal ────────────────────────────────────────────────────────────
function UpgradeModal({currentTier,reason,onClose,currentUser,onUpgraded}) {
  const [loading,setLoading]=useState(null);
  const checkout=async(tier)=>{
    if(!currentUser?.email)return;
    setLoading(tier);
    try {
      const res=await fetch(`${BACKEND_URL}/api/stripe/checkout`,{method:"POST",
        headers:{"Content-Type":"application/json"},
        body:JSON.stringify({tier,userId:currentUser.id,email:currentUser.email,
          successUrl:window.location.href+"?billing=success",
          cancelUrl:window.location.href})});
      const data=await res.json();
      if(data.url) window.location.href=data.url;
    }catch(e){console.error(e);}
    setLoading(null);
  };
  const tiers=["starter","pro","team"];
  return(
    <div style={{position:"fixed",inset:0,background:"#000D",zIndex:300,display:"flex",alignItems:"center",justifyContent:"center"}}>
      <div style={{background:"#111113",border:"1px solid #1C1C1F",borderRadius:16,width:700,maxHeight:"90vh",overflow:"auto",padding:32,boxShadow:"0 40px 100px #0009"}}>
        <div style={{textAlign:"center",marginBottom:28}}>
          <div style={{fontSize:11,letterSpacing:3,color:"#C06149",fontFamily:"'DM Mono',monospace",marginBottom:8}}>LIMIT REACHED</div>
          <div style={{fontSize:24,fontWeight:800,color:"#E8E6E1",marginBottom:8}}>{reason}</div>
          <div style={{fontSize:13,color:"#6B6B70"}}>Upgrade your plan to continue</div>
        </div>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:16,marginBottom:24}}>
          {tiers.map(tier=>{
            const t=TIER_PRICES[tier];
            const lim=TIER_LIMITS[tier];
            const isCurrent=tier===currentTier;
            const isUpgrade=tiers.indexOf(tier)>tiers.indexOf(currentTier||"starter");
            return(
              <div key={tier} style={{background:isUpgrade?"#141416":"#111113",border:`1px solid ${isUpgrade?"#C9A96E33":"#1C1C1F"}`,borderRadius:12,padding:20,textAlign:"center"}}>
                <div style={{fontSize:10,letterSpacing:3,color:isUpgrade?"#C9A96E":"#45454A",fontFamily:"'DM Mono',monospace",marginBottom:6}}>{t.label.toUpperCase()}</div>
                <div style={{fontSize:28,fontWeight:800,color:"#E8E6E1",marginBottom:4}}>{t.price}</div>
                <div style={{fontSize:10,color:"#45454A",marginBottom:16}}>/month</div>
                <div style={{fontSize:11,color:"#6B6B70",marginBottom:16,lineHeight:1.8,textAlign:"left"}}>
                  <div>👥 {lim.members} member{lim.members>1?"s":""}</div>
                  <div>🏘 {lim.markets===-1?"Unlimited":lim.markets} market{lim.markets!==1?"s":""}</div>
                  <div>🏠 {lim.leads===-1?"Unlimited":lim.leads} leads</div>
                  <div>📍 {lim.rentcast}/mo comps</div>
                  <div>💬 {lim.sms===0?"No":lim.sms+"/mo"} SMS</div>
                </div>
                {isCurrent
                  ? <div style={{background:"#1C1C1F",borderRadius:8,padding:"8px",fontSize:11,color:"#45454A",fontFamily:"'DM Mono',monospace"}}>CURRENT PLAN</div>
                  : isUpgrade
                  ? <button onClick={()=>checkout(tier)} disabled={!!loading} style={{width:"100%",background:"linear-gradient(135deg,#C9A96E,#A8834A)",border:"none",borderRadius:8,color:"#0F0F11",padding:"10px",fontSize:12,cursor:loading?"not-allowed":"pointer",fontWeight:800,fontFamily:"'DM Mono',monospace"}}>
                      {loading===tier?"LOADING...":"UPGRADE →"}
                    </button>
                  : null
                }
              </div>
            );
          })}
        </div>
        <div style={{textAlign:"center"}}>
          <button onClick={onClose} style={{background:"none",border:"1px solid #2A2A2E",borderRadius:8,color:"#6B6B70",padding:"10px 24px",cursor:"pointer",fontSize:13}}>Maybe later</button>
        </div>
      </div>
    </div>
  );
}


// ─── Mobile Detection Hook ────────────────────────────────────────────────────
function useMobile() {
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768);
  useEffect(() => {
    const h = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", h);
    return () => window.removeEventListener("resize", h);
  }, []);
  return isMobile;
}

// ─── Mobile Lead Card ─────────────────────────────────────────────────────────
function MobileLeadCard({ lead, rehabConfig, maoFormula, onClick }) {
  const stage = stageOf(lead.stage);
  const rehabCost = calcRehab(lead.rehabTier, lead.sqft || 1400, rehabConfig, lead.market);
  const nextFU = (lead.followUps || []).filter(f => !f.done).sort((a, b) => new Date(a.ts) - new Date(b.ts))[0];
  const isOverdue = nextFU && new Date(nextFU.ts) < new Date();
  return (
    <div onClick={() => onClick(lead)} style={{
      background: "#111113", borderRadius: 12,
      border: `1px solid ${lead.hot ? "#C0614944" : "#1C1C1F"}`,
      borderLeft: `3px solid ${stage.color}`,
      padding: "14px 16px", marginBottom: 10, cursor: "pointer",
      WebkitTapHighlightColor: "transparent",
    }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 6 }}>
        <div style={{ flex: 1, paddingRight: 8 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 3 }}>
            {lead.hot && <span style={{ fontSize: 9, background: "#C06149", color: "#0F0F11", borderRadius: 3, padding: "1px 6px", fontWeight: 800, fontFamily: "'DM Mono',monospace" }}>🔥 HOT</span>}
            <span style={{ fontSize: 15, fontWeight: 700, color: "#E8E6E1" }}>{lead.name}</span>
          </div>
          <div style={{ fontSize: 11, color: "#6B6B70", lineHeight: 1.3 }}>{lead.address}</div>
        </div>
        <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 4, flexShrink: 0 }}>
          <span style={{ fontSize: 9, fontFamily: "'DM Mono',monospace", color: stage.color, background: stage.color + "18", border: `1px solid ${stage.color}33`, borderRadius: 4, padding: "2px 7px", whiteSpace: "nowrap" }}>{stage.icon} {stage.label.split(" ")[0].toUpperCase()}</span>
          <span style={{ fontSize: 13, fontWeight: 700, color: lead.score >= 80 ? "#C9A96E" : lead.score >= 55 ? "#C9935A" : "#45454A", fontFamily: "'DM Mono',monospace" }}>{lead.score}</span>
        </div>
      </div>
      <div style={{ display: "flex", gap: 4, flexWrap: "wrap", marginBottom: lead.arv ? 8 : 0 }}>
        <Tag label={`EQ ${lead.equity}%`} color="#C9A96E" />
        {lead.taxLien && <Tag label="TAX LIEN" color="#C06149" />}
        {lead.vacant && <Tag label="VACANT" color="#C9935A" />}
        {lead.market && <Tag label={lead.market.toUpperCase()} color="#C9935A" />}
        {lead.rehabTier && <Tag label={lead.rehabTier.toUpperCase()} color={lead.rehabTier === "light" ? "#5A8A60" : lead.rehabTier === "medium" ? "#C9935A" : "#C06149"} />}
      </div>
      {lead.arv && (
        <div style={{ display: "flex", gap: 6 }}>
          {[
            [lead.propertyType === "Land" ? "FMV" : "ARV", fmt$(lead.arv.value), "#C9A96E"],
            ...(lead.propertyType !== "Land" && lead.rehabTier ? [[" REPAIR", fmt$(rehabCost), "#C9935A"]] : []),
            ["OFFER", fmt$(calcMAO(lead, rehabCost, maoFormula)), "#5A8A60"],
          ].map(([k, v, c]) => (
            <div key={k} style={{ background: "#0F0F11", borderRadius: 5, padding: "5px 8px", flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 8, color: "#45454A", fontFamily: "'DM Mono',monospace" }}>{k}</div>
              <div style={{ fontSize: 12, fontWeight: 700, color: c, fontFamily: "'DM Mono',monospace", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{v}</div>
            </div>
          ))}
        </div>
      )}
      {lead.stage === "contract" && lead.wholesaleFee && (
        <div style={{ marginTop: 8, background: "#B87AA814", border: "1px solid #B87AA833", borderRadius: 6, padding: "5px 10px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span style={{ fontSize: 9, color: "#B87AA8", fontFamily: "'DM Mono',monospace", letterSpacing: 1 }}>★ WHOLESALE FEE</span>
          <span style={{ fontSize: 14, fontWeight: 800, color: "#B87AA8", fontFamily: "'DM Mono',monospace" }}>{fmt$(lead.wholesaleFee)}</span>
        </div>
      )}
      {nextFU && (
        <div style={{ marginTop: 6, fontSize: 10, color: isOverdue ? "#C06149" : "#C9935A", display: "flex", alignItems: "center", gap: 4 }}>
          {isOverdue ? "⚠" : "⏰"} {nextFU.due}
        </div>
      )}
    </div>
  );
}

// ─── Mobile Layout ────────────────────────────────────────────────────────────
function MobileLayout({
  leads, filtered, stats, selected, setSelected,
  rehabConfig, maoFormula, markets, activeMarket, setActiveMarket,
  search, setSearch, hotOnly, setHotOnly,
  activeStatFilter, setActiveStatFilter,
  showFollowUpsList, setShowFollowUpsList,
  showNewLead, setShowNewLead, showCSV, setShowCSV,
  saving, subscription, upgradePrompt, setUpgradePrompt,
  addLead, addLeads, updateLead, checkGate,
  currentUser, showRehabConfig, setShowRehabConfig,
  setMaoFormula, setRehabConfig, setMarkets, saveSettings,
}) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [stageFilter, setStageFilter] = useState("all");
  const stageFiltered = stageFilter === "all" ? filtered : filtered.filter(l => l.stage === stageFilter);

  const nav = (action) => { setMenuOpen(false); action(); };

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%", background: "#0A0A0B", fontFamily: "'DM Sans',sans-serif", color: "#E8E6E1", overflow: "hidden" }}>

      {/* ── Hamburger Menu ── */}
      {menuOpen && (
        <div style={{ position: "fixed", inset: 0, zIndex: 400 }}>
          <div style={{ position: "absolute", inset: 0, background: "#000A", zIndex: 0 }} onClick={() => setMenuOpen(false)} />
          <div style={{ position: "absolute", left: 0, top: 0, bottom: 0, width: 280, background: "#0F0F11", borderRight: "1px solid #1C1C1F", display: "flex", flexDirection: "column", boxShadow: "12px 0 40px #0009", zIndex: 1 }}>
            <div style={{ padding: "32px 20px 20px", borderBottom: "1px solid #1C1C1F" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                <div>
                  <div style={{ fontSize: 9, letterSpacing: 3, color: "#C9A96E", fontFamily: "'DM Mono',monospace", marginBottom: 4 }}>COAST2COAST EQUITY</div>
                  <div style={{ fontSize: 18, fontWeight: 800, color: "#E8E6E1", letterSpacing: 1 }}>WholesaleIQ</div>
                  <div style={{ fontSize: 11, color: "#6B6B70", marginTop: 4 }}>{currentUser?.email}</div>
                </div>
                <button onClick={() => setMenuOpen(false)} style={{ background: "none", border: "none", color: "#6B6B70", fontSize: 22, cursor: "pointer", padding: 4, marginTop: 2 }}>✕</button>
              </div>
              <div style={{ marginTop: 14, background: "#C9A96E14", border: "1px solid #C9A96E33", borderRadius: 6, padding: "6px 12px", display: "inline-flex", alignItems: "center", gap: 6 }}>
                <div style={{ width: 5, height: 5, borderRadius: "50%", background: "#C9A96E" }} />
                <span style={{ fontSize: 9, fontFamily: "'DM Mono',monospace", color: "#C9A96E", fontWeight: 700, letterSpacing: 1 }}>{(subscription?.tier || "STARTER").toUpperCase()} PLAN</span>
              </div>
            </div>
            <div style={{ flex: 1, overflowY: "auto", padding: "12px 12px" }}>
              {[
                { icon: "📋", label: "Pipeline", action: () => setMenuOpen(false) },
                { icon: "⏰", label: "Follow-Ups", badge: stats.followUps > 0 ? stats.followUps : null, action: () => nav(() => setShowFollowUpsList(true)) },
                { icon: "📂", label: "Import CSV", action: () => nav(() => setShowCSV(true)) },
                { icon: "⚙", label: "Settings & Rehab", action: () => nav(() => setShowRehabConfig(true)) },
              ].map((item, i) => (
                <button key={i} onClick={item.action} style={{
                  width: "100%", background: "none", border: "none", borderRadius: 10,
                  padding: "15px 16px", textAlign: "left", cursor: "pointer",
                  color: "#C4C2BD", fontSize: 15, fontWeight: 600,
                  display: "flex", alignItems: "center", justifyContent: "space-between",
                  marginBottom: 2,
                }}>
                  <span>{item.icon} {item.label}</span>
                  {item.badge != null && (
                    <span style={{ background: "#C9935A", color: "#0F0F11", borderRadius: 10, fontSize: 10, fontWeight: 800, padding: "2px 8px", fontFamily: "'DM Mono',monospace" }}>{item.badge}</span>
                  )}
                </button>
              ))}
            </div>
            <div style={{ padding: 16, borderTop: "1px solid #1C1C1F" }}>
              <button onClick={async () => { await supabase.auth.signOut(); window.location.reload(); }}
                style={{ width: "100%", background: "none", border: "1px solid #2A2A2E", borderRadius: 10, padding: "13px 16px", cursor: "pointer", color: "#6B6B70", fontSize: 14 }}>
                Sign Out
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Top Header ── */}
      <div style={{ padding: "14px 16px", background: "#0F0F11", borderBottom: "1px solid #1C1C1F", display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0 }}>
        <button onClick={() => setMenuOpen(true)} style={{ background: "none", border: "none", color: "#C4C2BD", fontSize: 22, cursor: "pointer", padding: "4px 8px 4px 0", lineHeight: 1 }}>☰</button>
        <div style={{ textAlign: "center" }}>
          <div style={{ fontSize: 11, letterSpacing: 3, color: "#C9A96E", fontFamily: "'DM Mono',monospace", fontWeight: 700 }}>WHOLESALEIQ</div>
          <div style={{ fontSize: 9, color: "#45454A", fontFamily: "'DM Mono',monospace", marginTop: 1 }}>{leads.length} LEADS · <span style={{ color: saving ? "#C9935A" : "#5A8A60" }}>{saving ? "SAVING" : "SAVED"}</span></div>
        </div>
        <button onClick={() => setShowNewLead(true)} style={{ background: "linear-gradient(135deg,#C9A96E,#A8834A)", border: "none", borderRadius: 8, color: "#0F0F11", padding: "8px 14px", fontSize: 12, fontWeight: 800, cursor: "pointer", fontFamily: "'DM Mono',monospace" }}>+ NEW</button>
      </div>

      {/* ── Stats Strip ── */}
      <div style={{ display: "flex", overflowX: "auto", background: "#0F0F11", borderBottom: "1px solid #1C1C1F", flexShrink: 0, scrollbarWidth: "none" }}>
        <style>{`.mstat::-webkit-scrollbar{display:none}`}</style>
        {[
          { label: "NEW LEADS", value: stats.newLeads, color: "#C4C2BD", filter: "new" },
          { label: "🔥 HOT", value: stats.hot, color: "#C06149", filter: "hot" },
          { label: "★ CONTRACT", value: stats.contract, color: "#B87AA8", filter: "contract" },
          { label: "EST. FEES", value: fmt$(stats.fees), color: "#5A8A60", filter: null },
          { label: "⏰ DUE", value: stats.followUps, color: "#C9935A", filter: "followups" },
        ].map((s, i) => {
          const isActive = activeStatFilter === s.filter && s.filter !== null;
          return (
            <div key={i} onClick={() => {
              if (s.filter === "followups") { setShowFollowUpsList(true); return; }
              if (!s.filter) return;
              setActiveStatFilter(activeStatFilter === s.filter ? null : s.filter);
            }} style={{
              padding: "10px 16px", flexShrink: 0, cursor: s.filter ? "pointer" : "default",
              borderBottom: isActive ? `2px solid ${s.color}` : "2px solid transparent",
              background: isActive ? s.color + "11" : "transparent",
              transition: "background .15s",
            }}>
              <div style={{ fontSize: 8, letterSpacing: 2, color: isActive ? s.color : "#2A2A2E", fontFamily: "'DM Mono',monospace", whiteSpace: "nowrap" }}>{s.label}</div>
              <div style={{ fontSize: 17, fontWeight: 800, color: s.color, fontFamily: "'DM Mono',monospace" }}>{s.value}</div>
            </div>
          );
        })}
        {activeStatFilter && (
          <button onClick={() => setActiveStatFilter(null)} style={{ background: "none", border: "none", color: "#45454A", cursor: "pointer", fontSize: 10, padding: "0 14px", flexShrink: 0, fontFamily: "'DM Mono',monospace" }}>✕</button>
        )}
      </div>

      {/* ── Stage Filter Tabs ── */}
      <div style={{ display: "flex", overflowX: "auto", background: "#111113", borderBottom: "1px solid #1C1C1F", flexShrink: 0, padding: "0 12px", scrollbarWidth: "none" }}>
        {[{ id: "all", label: "ALL", color: "#C4C2BD", icon: "" }, ...STAGES].map(s => (
          <button key={s.id} onClick={() => setStageFilter(s.id)} style={{
            background: "none", border: "none",
            borderBottom: stageFilter === s.id ? `2px solid ${s.color || "#C4C2BD"}` : "2px solid transparent",
            padding: "10px 12px", cursor: "pointer", flexShrink: 0,
            color: stageFilter === s.id ? (s.color || "#C4C2BD") : "#45454A",
            fontSize: 10, fontFamily: "'DM Mono',monospace", fontWeight: 700, letterSpacing: 1, whiteSpace: "nowrap",
          }}>
            {s.id === "all" ? "ALL" : (s.icon + " " + s.label.split(" ")[0].toUpperCase())}
          </button>
        ))}
      </div>

      {/* ── Search Bar ── */}
      <div style={{ padding: "10px 14px", background: "#111113", borderBottom: "1px solid #1C1C1F", display: "flex", gap: 8, flexShrink: 0 }}>
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="🔍  Search leads..."
          style={{ flex: 1, background: "#0F0F11", border: "1px solid #2A2A2E", borderRadius: 8, padding: "9px 14px", color: "#C4C2BD", fontSize: 13, outline: "none" }} />
        <button onClick={() => setHotOnly(!hotOnly)}
          style={{ background: hotOnly ? "#C0614922" : "none", border: `1px solid ${hotOnly ? "#C06149" : "#2A2A2E"}`, borderRadius: 8, color: hotOnly ? "#C06149" : "#6B6B70", padding: "9px 13px", cursor: "pointer", fontSize: 15 }}>
          🔥
        </button>
        {markets.length > 0 && (
          <select value={activeMarket} onChange={e => setActiveMarket(e.target.value)}
            style={{ background: "#0F0F11", border: `1px solid ${activeMarket !== "all" ? "#C9935A44" : "#2A2A2E"}`, borderRadius: 8, padding: "9px 10px", color: activeMarket !== "all" ? "#C9935A" : "#6B6B70", fontSize: 11, fontFamily: "'DM Mono',monospace", outline: "none", cursor: "pointer" }}>
            <option value="all">MKT</option>
            {markets.map(m => <option key={m} value={m}>{m.toUpperCase()}</option>)}
          </select>
        )}
      </div>

      {/* ── Lead List ── */}
      <div style={{ flex: 1, overflowY: "auto", padding: "12px 14px", WebkitOverflowScrolling: "touch" }}>
        {stageFiltered.length === 0 && (
          <div style={{ textAlign: "center", padding: "60px 24px" }}>
            <div style={{ fontSize: 40, marginBottom: 12 }}>📋</div>
            <div style={{ fontSize: 12, color: "#45454A", fontFamily: "'DM Mono',monospace" }}>NO LEADS FOUND</div>
            {(search || stageFilter !== "all" || activeStatFilter || hotOnly) && (
              <button onClick={() => { setSearch(""); setStageFilter("all"); setActiveStatFilter(null); setHotOnly(false); }}
                style={{ marginTop: 16, background: "none", border: "1px solid #2A2A2E", borderRadius: 8, color: "#6B6B70", padding: "8px 16px", cursor: "pointer", fontSize: 12, fontFamily: "'DM Mono',monospace" }}>
                CLEAR FILTERS
              </button>
            )}
          </div>
        )}
        {stageFiltered.map(lead => (
          <MobileLeadCard key={lead.id} lead={lead} rehabConfig={rehabConfig} maoFormula={maoFormula} onClick={setSelected} />
        ))}
        <div style={{ height: 20 }} />
      </div>

      {/* ── Modals ── */}
      {selected && (
        <LeadModal
          lead={leads.find(l => l.id === selected.id) || selected}
          rehabConfig={rehabConfig} maoFormula={maoFormula}
          onClose={() => setSelected(null)}
          onUpdate={u => { updateLead(u); setSelected(u); }}
          markets={markets}
        />
      )}
      {showNewLead && <NewLeadModal onClose={() => setShowNewLead(false)} onAdd={addLead} markets={markets} />}
      {showCSV && <CSVImportModal onClose={() => setShowCSV(false)} onImport={addLeads} />}
      {upgradePrompt && <UpgradeModal currentTier={subscription?.tier} reason={upgradePrompt} onClose={() => setUpgradePrompt(null)} currentUser={currentUser} onUpgraded={() => {}} />}
      {showRehabConfig && (
        <RehabConfigModal config={rehabConfig} maoFormula={maoFormula}
          onMaoFormulaChange={f => { setMaoFormula(f); saveSettings(markets, rehabConfig, f); }}
          onSave={(cfg, mkts) => { setRehabConfig(cfg); setMarkets(mkts); saveSettings(mkts, cfg, maoFormula); }}
          onClose={() => setShowRehabConfig(false)} markets={markets}
          onMarketsChange={m => { if (m.length > markets.length && !checkGate("markets", markets.length)) return; setMarkets(m); }}
        />
      )}
      {showFollowUpsList && (
        <div style={{ position: "fixed", inset: 0, background: "#000C", zIndex: 200 }} onClick={e => e.target === e.currentTarget && setShowFollowUpsList(false)}>
          <div style={{ position: "absolute", right: 0, top: 0, bottom: 0, width: "92%", background: "#111113", borderLeft: "1px solid #1C1C1F", display: "flex", flexDirection: "column", boxShadow: "-20px 0 60px #0009" }}>
            <div style={{ padding: "20px 20px 16px", borderBottom: "1px solid #1C1C1F", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div>
                <div style={{ fontSize: 10, letterSpacing: 3, color: "#C9935A", fontFamily: "'DM Mono',monospace", marginBottom: 4 }}>PIPELINE</div>
                <div style={{ fontSize: 18, fontWeight: 800, color: "#E8E6E1" }}>⏰ Follow-Ups</div>
              </div>
              <button onClick={() => setShowFollowUpsList(false)} style={{ background: "none", border: "none", color: "#6B6B70", cursor: "pointer", fontSize: 22 }}>✕</button>
            </div>
            <div style={{ overflowY: "auto", flex: 1, padding: 16 }}>
              {leads.filter(l => (l.followUps || []).some(fu => !fu.done)).length === 0 && (
                <div style={{ textAlign: "center", color: "#2A2A2E", fontSize: 12, padding: "40px 0", fontFamily: "'DM Mono',monospace" }}>No pending follow-ups</div>
              )}
              {leads.filter(l => (l.followUps || []).some(fu => !fu.done))
                .sort((a, b) => {
                  const aTs = (a.followUps || []).filter(f => !f.done).sort((x, y) => new Date(x.ts) - new Date(y.ts))[0]?.ts || "";
                  const bTs = (b.followUps || []).filter(f => !f.done).sort((x, y) => new Date(x.ts) - new Date(y.ts))[0]?.ts || "";
                  return new Date(aTs) - new Date(bTs);
                })
                .map(lead => {
                  const pending = (lead.followUps || []).filter(fu => !fu.done).sort((a, b) => new Date(a.ts) - new Date(b.ts));
                  const stage = stageOf(lead.stage);
                  return (
                    <div key={lead.id} style={{ background: "#111113", border: "1px solid #1C1C1F", borderRadius: 10, padding: 14, marginBottom: 10 }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 8, cursor: "pointer" }}
                        onClick={() => { setSelected(lead); setShowFollowUpsList(false); }}>
                        <div>
                          <div style={{ fontSize: 13, fontWeight: 700, color: "#E8E6E1" }}>{lead.name}</div>
                          <div style={{ fontSize: 10, color: "#45454A", marginTop: 2 }}>{lead.address}</div>
                        </div>
                        <span style={{ fontSize: 9, fontFamily: "'DM Mono',monospace", color: stage.color, background: stage.color + "18", border: `1px solid ${stage.color}33`, borderRadius: 4, padding: "2px 7px" }}>{stage.label}</span>
                      </div>
                      {pending.map((fu, i) => {
                        const isOvd = fu.ts && new Date(fu.ts) < new Date();
                        return (
                          <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 10px", background: isOvd ? "#C0614908" : "#0F0F11", border: `1px solid ${isOvd ? "#C0614933" : "#1C1C1F"}`, borderRadius: 7, marginBottom: 6 }}>
                            <input type="checkbox" onChange={() => {
                              const updated = lead.followUps.map((f, idx) => lead.followUps.indexOf(fu) === idx ? { ...f, done: true } : f);
                              updateLead({ ...lead, followUps: updated });
                            }} style={{ accentColor: "#5A8A60", cursor: "pointer", width: 18, height: 18 }} />
                            <div style={{ flex: 1 }}>
                              <div style={{ fontSize: 13, color: isOvd ? "#C06149" : "#C4C2BD", fontWeight: 600 }}>{fu.note}</div>
                              <div style={{ fontSize: 10, color: isOvd ? "#C06149" : "#C9935A", fontFamily: "'DM Mono',monospace", marginTop: 2 }}>{isOvd ? "⚠ OVERDUE · " : ""}{fu.due}</div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  );
                })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Main App ─────────────────────────────────────────────────────────────────

// ─── Invite Modal ─────────────────────────────────────────────────────────────
function InviteModal({ currentUser, onClose }) {
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('member');
  const [inviteLink, setInviteLink] = useState('');
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState('');

  const handleInvite = async () => {
    if (!email) return;
    setLoading(true); setError('');
    try {
      const res = await fetch(`${BACKEND_URL}/api/invite`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          teamId: currentUser.team_id,
          email,
          role,
          inviterName: currentUser.email
        })
      });
      const data = await res.json();
      if (data.inviteLink) setInviteLink(data.inviteLink);
      else setError(data.error || 'Failed to generate link');
    } catch(e) { setError(e.message); }
    setLoading(false);
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(inviteLink);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const inp = { width:'100%', padding:'10px 14px', background:'#0F0F11', border:'1px solid #2A2A2E', borderRadius:8, color:'#E8E6E1', fontSize:14, outline:'none', boxSizing:'border-box' };

  return (
    <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.75)',zIndex:500,display:'flex',alignItems:'center',justifyContent:'center'}}
      onClick={e=>e.target===e.currentTarget&&onClose()}>
      <div style={{background:'#111113',border:'1px solid #C9A96E44',borderRadius:14,padding:32,width:440,maxWidth:'92vw',boxShadow:'0 30px 80px #0009'}}>
        <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:24}}>
          <div>
            <div style={{fontSize:9,letterSpacing:3,color:'#C9A96E',fontFamily:"'DM Mono',monospace",marginBottom:4}}>TEAM MANAGEMENT</div>
            <div style={{fontSize:20,fontWeight:800,color:'#E8E6E1'}}>Invite Member</div>
          </div>
          <button onClick={onClose} style={{background:'none',border:'none',color:'#6B6B70',fontSize:22,cursor:'pointer'}}>✕</button>
        </div>

        <div style={{marginBottom:12}}>
          <div style={{fontSize:11,color:'#6B6B70',marginBottom:6,fontFamily:"'DM Mono',monospace"}}>EMAIL ADDRESS</div>
          <input style={inp} placeholder="teammate@email.com" value={email} onChange={e=>setEmail(e.target.value)}
            onKeyDown={e=>e.key==='Enter'&&handleInvite()} />
        </div>

        <div style={{marginBottom:24}}>
          <div style={{fontSize:11,color:'#6B6B70',marginBottom:6,fontFamily:"'DM Mono',monospace"}}>ROLE</div>
          <select style={{...inp,cursor:'pointer'}} value={role} onChange={e=>setRole(e.target.value)}>
            <option value="member">Acquisitions — full CRM access</option>
            <option value="cold_caller">Cold Caller — lead intake only</option>
            <option value="admin">Admin — manage team + full access</option>
          </select>
        </div>

        {error && <div style={{background:'#C0614911',border:'1px solid #C0614933',borderRadius:8,padding:'10px 14px',marginBottom:16,color:'#C06149',fontSize:12}}>⚠ {error}</div>}

        <button onClick={handleInvite} disabled={loading||!email}
          style={{width:'100%',padding:'12px',background:'linear-gradient(135deg,#C9A96E,#A8834A)',border:'none',borderRadius:8,color:'#0F0F11',fontWeight:800,fontSize:14,cursor:'pointer',fontFamily:"'DM Mono',monospace",opacity:(loading||!email)?0.5:1,marginBottom:inviteLink?16:0}}>
          {loading ? 'GENERATING...' : 'GENERATE INVITE LINK'}
        </button>

        {inviteLink && (
          <div style={{background:'#0F0F11',border:'1px solid #C9A96E33',borderRadius:8,padding:14}}>
            <div style={{fontSize:10,color:'#6B6B70',marginBottom:8,fontFamily:"'DM Mono',monospace"}}>SHARE THIS LINK — EXPIRES IN 7 DAYS</div>
            <div style={{display:'flex',gap:8,alignItems:'flex-start'}}>
              <code style={{flex:1,color:'#C9A96E',fontSize:11,wordBreak:'break-all',lineHeight:1.5}}>{inviteLink}</code>
              <button onClick={handleCopy}
                style={{background:copied?'#5A8A60':'#C9A96E',color:'#0F0F11',border:'none',borderRadius:6,padding:'6px 12px',fontSize:11,cursor:'pointer',fontWeight:700,whiteSpace:'nowrap',flexShrink:0,fontFamily:"'DM Mono',monospace"}}>
                {copied ? '✓ COPIED' : 'COPY'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Cold Caller Intake ───────────────────────────────────────────────────────
function ColdCallerIntake({ currentUser }) {
  const [form, setForm] = useState({ name:'', phone:'', address:'', askingPrice:'', condition:'fair', motivation:'warm', notes:'' });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const set = (k, v) => setForm(f => ({...f, [k]: v}));

  const handleSubmit = async () => {
    if (!form.name || !form.phone || !form.address) { setError('Name, phone, and address are required.'); return; }
    setLoading(true); setError('');
    const { error: err } = await supabase.from('leads').insert({
      name: form.name, phone: form.phone, address: form.address,
      equity: parseInt(form.askingPrice) || 0,
      condition_report: form.condition,
      notes: form.notes,
      stage: 'new',
      hot: form.motivation === 'hot',
      user_id: currentUser.id,
      org_id: currentUser.team_id || null,
    });
    setLoading(false);
    if (err) setError('Submit failed: ' + err.message);
    else setSubmitted(true);
  };

  const inp = { width:'100%', padding:'13px 14px', background:'#111113', border:'1px solid #1C1C1F', borderRadius:8, color:'#E8E6E1', fontSize:15, outline:'none', boxSizing:'border-box', marginBottom:12 };

  if (submitted) return (
    <div style={{minHeight:'100vh',background:'#0A0A0B',display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',gap:16,fontFamily:"'DM Sans',sans-serif"}}>
      <div style={{fontSize:72}}>✅</div>
      <div style={{fontSize:9,letterSpacing:3,color:'#C9A96E',fontFamily:"'DM Mono',monospace"}}>WHOLESALEIQ</div>
      <h2 style={{color:'#E8E6E1',fontSize:26,margin:0,fontWeight:800}}>Lead Submitted!</h2>
      <p style={{color:'#6B6B70',margin:0}}>Your manager will review it shortly.</p>
      <button onClick={()=>{setSubmitted(false);setForm({name:'',phone:'',address:'',askingPrice:'',condition:'fair',motivation:'warm',notes:''});}}
        style={{marginTop:8,background:'linear-gradient(135deg,#C9A96E,#A8834A)',color:'#0F0F11',border:'none',borderRadius:8,padding:'12px 28px',fontWeight:800,fontSize:15,cursor:'pointer',fontFamily:"'DM Mono',monospace"}}>
        SUBMIT ANOTHER
      </button>
    </div>
  );

  return (
    <div style={{minHeight:'100vh',background:'#0A0A0B',display:'flex',alignItems:'center',justifyContent:'center',padding:20,fontFamily:"'DM Sans',sans-serif"}}>
      <div style={{width:'100%',maxWidth:480,background:'#111113',border:'1px solid #1C1C1F',borderRadius:16,padding:32,boxShadow:'0 30px 80px #0009'}}>
        <div style={{textAlign:'center',marginBottom:28}}>
          <div style={{fontSize:9,letterSpacing:3,color:'#C9A96E',fontFamily:"'DM Mono',monospace",marginBottom:6}}>WHOLESALEIQ</div>
          <h1 style={{color:'#E8E6E1',fontSize:22,margin:0,fontWeight:800}}>Lead Intake</h1>
          <p style={{color:'#6B6B70',fontSize:13,marginTop:6,marginBottom:0}}>Logged in as {currentUser?.email}</p>
        </div>

        <input style={inp} placeholder="Seller Name *" value={form.name} onChange={e=>set('name',e.target.value)} />
        <input style={inp} placeholder="Phone Number *" type="tel" value={form.phone} onChange={e=>set('phone',e.target.value)} />
        <input style={inp} placeholder="Property Address *" value={form.address} onChange={e=>set('address',e.target.value)} />
        <input style={inp} placeholder="Asking Price" type="number" value={form.askingPrice} onChange={e=>set('askingPrice',e.target.value)} />

        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12,marginBottom:12}}>
          <div>
            <div style={{fontSize:10,color:'#6B6B70',marginBottom:5,fontFamily:"'DM Mono',monospace"}}>CONDITION</div>
            <select style={{...inp,marginBottom:0,cursor:'pointer'}} value={form.condition} onChange={e=>set('condition',e.target.value)}>
              <option value="good">Good</option>
              <option value="fair">Fair</option>
              <option value="poor">Poor / Distressed</option>
            </select>
          </div>
          <div>
            <div style={{fontSize:10,color:'#6B6B70',marginBottom:5,fontFamily:"'DM Mono',monospace"}}>MOTIVATION</div>
            <select style={{...inp,marginBottom:0,cursor:'pointer'}} value={form.motivation} onChange={e=>set('motivation',e.target.value)}>
              <option value="hot">🔥 Hot</option>
              <option value="warm">Warm</option>
              <option value="cold">Cold</option>
            </select>
          </div>
        </div>

        <textarea style={{...inp,resize:'vertical',minHeight:90}} placeholder="Notes / Seller situation..." value={form.notes} onChange={e=>set('notes',e.target.value)} />

        {error && <div style={{background:'#C0614911',border:'1px solid #C0614933',borderRadius:8,padding:'10px 14px',marginBottom:12,color:'#C06149',fontSize:12}}>⚠ {error}</div>}

        <button onClick={handleSubmit} disabled={loading}
          style={{width:'100%',padding:'14px',background:'linear-gradient(135deg,#C9A96E,#A8834A)',border:'none',borderRadius:8,color:'#0F0F11',fontWeight:800,fontSize:16,cursor:'pointer',fontFamily:"'DM Mono',monospace",letterSpacing:1,opacity:loading?0.6:1}}>
          {loading ? 'SUBMITTING...' : 'SUBMIT LEAD'}
        </button>
      </div>
    </div>
  );
}

export default function App({ currentUser, onLeadsChange }) {
  const [leads,setLeads]             = useState([]);
  const [selected,setSelected]       = useState(null);
  const [dragging,setDragging]       = useState(null);
  const [rehabConfig,setRehabConfig] = useState({__default__: DEFAULT_REHAB});
  const [markets,setMarkets]         = useState([]);
  const [activeMarket,setActiveMarket] = useState("all");
  const [showRehabConfig,setShowRehabConfig] = useState(false);
  const [showNewLead,setShowNewLead] = useState(false);
  const [showCSV,setShowCSV]         = useState(false);
  const [search,setSearch]           = useState("");
  const [hotOnly,setHotOnly]         = useState(false);
  const [activeStatFilter,setActiveStatFilter] = useState(null); // null | "new" | "hot" | "contract" | "followups"
  const [showFollowUpsList,setShowFollowUpsList] = useState(false);
  const [saving,setSaving]           = useState(false);
  const [dbReady,setDbReady]         = useState(false);
  const [subscription,setSubscription] = useState({tier:"starter",status:"none",limits:TIER_LIMITS.starter});
  const [upgradePrompt,setUpgradePrompt] = useState(null);
  const [showInviteModal,setShowInviteModal] = useState(false);
  const [stageFilter,setStageFilter] = useState("all");
  const [maoFormula,setMaoFormula] = useState({singleFamily:{multiplier:70,closingCosts:0,minProfit:0},land:{multiplier:50},multiFamily:{multiplier:65,closingCosts:0,minProfit:0},commercial:{multiplier:60,closingCosts:0,minProfit:0}});

  const isMobile = useMobile();
  const userId = currentUser?.id;

  // ── Load leads from Supabase on mount ──────────────────────────────────────
  useEffect(()=>{
    if(!userId){setDbReady(true);return;}
    const timeout=setTimeout(()=>setDbReady(true),8000); // safety fallback
    Promise.all([
      supabase.from("leads").select("*").eq("user_id",currentUser?.id).order("created_at",{ascending:false}),
      supabase.from("user_settings").select("*").eq("user_id",userId).maybeSingle()
    ]).then(([leadsRes,settingsRes])=>{
      clearTimeout(timeout);
      if(!leadsRes.error){
        const loaded=(leadsRes.data||[]).map(fromRow);
        setLeads(loaded);
        onLeadsChange?.(loaded);
      }
      if(!settingsRes.error&&settingsRes.data){
        const s=settingsRes.data;
        if(s.markets&&s.markets.length) setMarkets(s.markets);
        if(s.rehab_config&&Object.keys(s.rehab_config).length) setRehabConfig(s.rehab_config);
        if(s.mao_formula&&Object.keys(s.mao_formula).length) setMaoFormula(s.mao_formula);
      }
      setDbReady(true);
    }).catch(err=>{
      clearTimeout(timeout);
      console.error("Load error:",err);
      setDbReady(true);
    });
    return()=>clearTimeout(timeout);
  },[userId]);

  const saveSettings=async(newMarkets,newRehabConfig,newMaoFormula)=>{
    if(!userId)return;
    await supabase.from("user_settings").upsert({
      user_id:userId,
      markets:newMarkets,
      rehab_config:newRehabConfig,
      mao_formula:newMaoFormula||maoFormula,
      updated_at:new Date().toISOString()
    },{onConflict:"user_id"});
  };

  // ── Load subscription ─────────────────────────────────────────────────────
  useEffect(()=>{
    if(!userId)return;
    fetch(`${BACKEND_URL}/api/stripe/subscription/${userId}`)
      .then(r=>r.json())
      .then(data=>setSubscription({...data,limits:TIER_LIMITS[data.tier]||TIER_LIMITS.starter}))
      .catch(()=>{});
  },[userId]);

  // ── Gate checker ───────────────────────────────────────────────────────────
  const checkGate=(type,current)=>{
    const lim=subscription.limits?.[type]??-1;
    if(lim===-1)return true;
    if(current>=lim){
      const msgs={leads:`You've reached your ${lim}-lead limit`,markets:`Your plan allows ${lim} market${lim>1?"s":""}`,rentcast:`You've used all ${lim} Rentcast calls this month`,sms:`You've used all ${lim} SMS messages this month`};
      setUpgradePrompt(msgs[type]||"You've reached your plan limit");
      return false;
    }
    return true;
  };

  // ── Save single lead to Supabase ───────────────────────────────────────────
  const saveLead=async(lead)=>{
    if(!userId)return;
    setSaving(true);
    const {error}=await supabase.from("leads").upsert(toRow(lead,userId,currentUser?.org_id));
    if(error)console.error("Save error:",error);
    setTimeout(()=>setSaving(false),800);
  };

  // ── Delete lead from Supabase ──────────────────────────────────────────────
  const deleteLead=async(id)=>{
    if(!userId)return;
    await supabase.from("leads").delete().eq("id",id).eq("user_id",userId);
  };

  const setLeadsAndSync=(updater)=>{
    setLeads(prev=>{
      const next=typeof updater==="function"?updater(prev):updater;
      onLeadsChange?.(next);
      return next;
    });
  };

  const addLead=async(lead)=>{
    if(!checkGate("leads",leads.length))return;
    setLeadsAndSync(ls=>[lead,...ls]);
    await saveLead(lead);
  };

  const addLeads=async(batch)=>{
    setLeadsAndSync(ls=>[...batch,...ls]);
    setSaving(true);
    if(userId){
      const rows=batch.map(l=>toRow(l,userId,currentUser?.org_id));
      const {error}=await supabase.from("leads").upsert(rows);
      if(error)console.error("Batch save error:",error);
    }
    setTimeout(()=>setSaving(false),800);
  };

  const updateLead=async(upd)=>{
    setLeadsAndSync(ls=>ls.map(l=>l.id===upd.id?upd:l));
    await saveLead(upd);
  };

  const onDragStart=(e,id)=>{setDragging(id);e.dataTransfer.effectAllowed="move";};
  const onDragOver=e=>{e.preventDefault();e.dataTransfer.dropEffect="move";};
  const onDrop=(e,stageId)=>{
    e.preventDefault();
    if(!dragging)return;
    const lead=leads.find(l=>l.id===dragging);
    if(lead){const updated={...lead,stage:stageId};setLeadsAndSync(ls=>ls.map(l=>l.id===dragging?updated:l));saveLead(updated);}
    setDragging(null);
  };

  const filtered=leads.filter(l=>{
    if(activeStatFilter==="new"&&l.stage!=="new")return false;
    if(activeStatFilter==="hot"&&!l.hot)return false;
    if(activeStatFilter==="contract"&&l.stage!=="contract")return false;
    if(hotOnly&&!l.hot)return false;
    if(activeMarket!=="all"&&l.market!==activeMarket)return false;
    if(!search)return true;
    return l.name.toLowerCase().includes(search.toLowerCase())||l.address.toLowerCase().includes(search.toLowerCase());
  });

  const stats={newLeads:leads.filter(l=>l.stage==="new").length,hot:leads.filter(l=>l.hot).length,contract:leads.filter(l=>l.stage==="contract").length,fees:leads.filter(l=>l.stage==="contract"&&l.wholesaleFee).reduce((s,l)=>s+(l.wholesaleFee||0),0),followUps:leads.filter(l=>(l.followUps||[]).some(fu=>!fu.done)).length};

  if(!dbReady)return(
    <div style={{flex:1,display:"flex",alignItems:"center",justifyContent:"center",background:"#0A0A0B",flexDirection:"column",gap:12}}>
      <div style={{width:32,height:32,border:"2px solid #1C1C1F",borderTopColor:"#C9A96E",borderRadius:"50%",animation:"spin .7s linear infinite"}}/>
      <div style={{fontSize:12,color:"#45454A",fontFamily:"'DM Mono',monospace"}}>LOADING LEADS...</div>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );

  // ── Role gate: cold callers see intake form only ──────────────────────────
  if(currentUser?.role === 'cold_caller') return <ColdCallerIntake currentUser={currentUser} />;

  if(isMobile) return (
    <MobileLayout
      leads={leads} filtered={filtered} stats={stats}
      selected={selected} setSelected={setSelected}
      rehabConfig={rehabConfig} maoFormula={maoFormula}
      markets={markets} activeMarket={activeMarket} setActiveMarket={setActiveMarket}
      search={search} setSearch={setSearch}
      hotOnly={hotOnly} setHotOnly={setHotOnly}
      activeStatFilter={activeStatFilter} setActiveStatFilter={setActiveStatFilter}
      showFollowUpsList={showFollowUpsList} setShowFollowUpsList={setShowFollowUpsList}
      showNewLead={showNewLead} setShowNewLead={setShowNewLead}
      showCSV={showCSV} setShowCSV={setShowCSV}
      saving={saving} subscription={subscription}
      upgradePrompt={upgradePrompt} setUpgradePrompt={setUpgradePrompt}
      addLead={addLead} addLeads={addLeads} updateLead={updateLead} checkGate={checkGate}
      currentUser={currentUser}
      showRehabConfig={showRehabConfig} setShowRehabConfig={setShowRehabConfig}
      setMaoFormula={setMaoFormula} setRehabConfig={setRehabConfig}
      setMarkets={setMarkets} saveSettings={saveSettings}
    />
  );

  return (
    <div style={{display:"flex",flexDirection:"column",flex:1,overflow:"hidden",background:"#0A0A0B",fontFamily:"'DM Sans',sans-serif",color:"#E8E6E1"}}>
      <div style={{borderBottom:"1px solid #1C1C1F",padding:"0 20px",background:"#0F0F11",display:"flex",alignItems:"center",gap:0,flexShrink:0}}>
        <div style={{display:"flex",gap:0,flex:1}}>
          {[
            {label:"NEW LEADS",value:stats.newLeads,color:"#C4C2BD",filter:"new"},
            {label:"🔥 HOT",value:stats.hot,color:"#C06149",filter:"hot"},
            {label:"★ CONTRACT",value:stats.contract,color:"#B87AA8",filter:"contract"},
            {label:"EST. ASSIGNMENT FEES",value:fmt$(stats.fees),color:"#5A8A60",filter:null},
            {label:"⏰ FOLLOW-UPS",value:stats.followUps,color:"#C9935A",filter:"followups"},
          ].map((s,i)=>{
            const isActive=activeStatFilter===s.filter&&s.filter!==null;
            return(
              <div key={i} onClick={()=>{
                if(s.filter==="followups"){setShowFollowUpsList(true);return;}
                if(!s.filter)return;
                setActiveStatFilter(activeStatFilter===s.filter?null:s.filter);
              }}
                style={{padding:"0 20px",borderLeft:i===0?"none":"1px solid #1C1C1F",cursor:s.filter?"pointer":"default",background:isActive?s.color+"11":"transparent",borderBottom:isActive?`2px solid ${s.color}`:"2px solid transparent",transition:"background .15s"}}>
                <div style={{fontSize:9,letterSpacing:2,color:isActive?s.color:"#2A2A2E",fontFamily:"'DM Mono',monospace",marginTop:2}}>{s.label}</div>
                <div style={{fontSize:18,fontWeight:800,color:s.color,fontFamily:"'DM Mono',monospace",paddingBottom:isActive?0:2}}>{s.value}</div>
              </div>
            );
          })}
          {activeStatFilter&&(
            <button onClick={()=>setActiveStatFilter(null)}
              style={{background:"none",border:"none",color:"#45454A",cursor:"pointer",fontSize:11,padding:"0 12px",fontFamily:"'DM Mono',monospace"}}>✕ clear</button>
          )}
        </div>
        <div style={{display:"flex",gap:8,alignItems:"center",padding:"10px 0"}}>
          <SavingDot saving={saving}/>
          {markets.length>0&&(
            <select value={activeMarket} onChange={e=>setActiveMarket(e.target.value)}
              style={{background:"#1C1C1F",border:`1px solid ${activeMarket!=="all"?"#C9935A44":"#2A2A2E"}`,borderRadius:8,padding:"7px 10px",color:activeMarket!=="all"?"#C9935A":"#6B6B70",fontSize:11,fontFamily:"'DM Mono',monospace",outline:"none",cursor:"pointer"}}>
              <option value="all">ALL MARKETS</option>
              {markets.map(m=><option key={m} value={m}>{m.toUpperCase()}</option>)}
            </select>
          )}
          <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search leads..." style={{background:"#1C1C1F",border:"1px solid #2A2A2E",borderRadius:8,padding:"7px 12px",color:"#C4C2BD",fontSize:12,width:150,outline:"none"}}/>
          <button onClick={()=>setHotOnly(!hotOnly)} style={{background:hotOnly?"#C0614922":"none",border:`1px solid ${hotOnly?"#C06149":"#2A2A2E"}`,borderRadius:8,color:hotOnly?"#C06149":"#6B6B70",padding:"7px 11px",cursor:"pointer",fontSize:11,fontFamily:"'DM Mono',monospace"}}>🔥</button>
          <button onClick={()=>setShowRehabConfig(true)} style={{background:"none",border:"1px solid #2A2A2E",borderRadius:8,color:"#6B6B70",padding:"7px 11px",cursor:"pointer",fontSize:11,fontFamily:"'DM Mono',monospace"}}>⚙</button>
          <button onClick={()=>setShowCSV(true)} style={{background:"#C9A96E14",border:"1px solid #C9A96E33",borderRadius:8,color:"#C9A96E",padding:"7px 12px",cursor:"pointer",fontSize:11,fontFamily:"'DM Mono',monospace",fontWeight:700}}>📂 CSV</button>
          <button onClick={()=>setShowNewLead(true)} style={{background:"linear-gradient(135deg,#C9A96E,#A8834A)",border:"none",borderRadius:8,color:"#0F0F11",padding:"7px 14px",cursor:"pointer",fontSize:12,fontWeight:800,fontFamily:"'DM Mono',monospace"}}>+ NEW LEAD</button>
        </div>
      </div>
      <div style={{display:"flex",gap:0,padding:"16px 12px",overflowX:"auto",flex:1,boxSizing:"border-box"}}>
        {STAGES.map(stage=>{
          const stageLeads=filtered.filter(l=>l.stage===stage.id);
          return(
            <div key={stage.id} onDragOver={onDragOver} onDrop={e=>onDrop(e,stage.id)} style={{minWidth:220,maxWidth:250,flex:"0 0 220px",margin:"0 5px",display:"flex",flexDirection:"column"}}>
              <div style={{padding:"9px 11px",marginBottom:8,borderRadius:8,background:"#0F0F11",border:`1px solid ${stage.color}33`}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                  <div style={{display:"flex",alignItems:"center",gap:6}}>
                    <div style={{width:6,height:6,borderRadius:"50%",background:stage.color}}/>
                    <span style={{fontSize:10,fontWeight:700,color:stage.color,fontFamily:"'DM Mono',monospace",letterSpacing:1}}>{stage.label.toUpperCase()}</span>
                  </div>
                  <div style={{fontSize:11,fontWeight:700,color:"#2A2A2E",fontFamily:"'DM Mono',monospace"}}>{stageLeads.length}</div>
                </div>
              </div>
              <div style={{flex:1,overflowY:"auto",paddingRight:2}}>
                {stageLeads.map(lead=><LeadCard key={lead.id} lead={lead} rehabConfig={rehabConfig} maoFormula={maoFormula} onClick={setSelected} onDragStart={onDragStart}/>)}
                {stageLeads.length===0&&<div style={{border:"1px dashed #1C1C1F",borderRadius:8,padding:"18px 12px",textAlign:"center",color:"#2A2A2E",fontSize:11,fontFamily:"'DM Mono',monospace"}}>DROP HERE</div>}
              </div>
            </div>
          );
        })}
      </div>
      {selected&&<LeadModal lead={leads.find(l=>l.id===selected.id)||selected} rehabConfig={rehabConfig} maoFormula={maoFormula} onClose={()=>setSelected(null)} onUpdate={u=>{updateLead(u);setSelected(u);}} markets={markets}/>}
      {showNewLead&&<NewLeadModal onClose={()=>setShowNewLead(false)} onAdd={addLead} markets={markets}/>}
      {showCSV&&<CSVImportModal onClose={()=>setShowCSV(false)} onImport={addLeads}/>}
      {upgradePrompt&&<UpgradeModal currentTier={subscription.tier} reason={upgradePrompt} onClose={()=>setUpgradePrompt(null)} currentUser={currentUser} onUpgraded={()=>{}}/>}
      {showInviteModal&&currentUser&&<InviteModal currentUser={currentUser} onClose={()=>setShowInviteModal(false)}/>}
      {showRehabConfig&&<RehabConfigModal config={rehabConfig} maoFormula={maoFormula} onMaoFormulaChange={f=>{setMaoFormula(f);saveSettings(markets,rehabConfig,f);}} onSave={(cfg,mkts)=>{setRehabConfig(cfg);setMarkets(mkts);saveSettings(mkts,cfg,maoFormula);}} onClose={()=>setShowRehabConfig(false)} markets={markets} onMarketsChange={m=>{if(m.length>markets.length&&!checkGate("markets",markets.length))return;setMarkets(m);if(activeMarket!=="all"&&!m.includes(activeMarket))setActiveMarket("all");}}/>}
      {showFollowUpsList&&(
        <div style={{position:"fixed",inset:0,background:"#000C",zIndex:200,display:"flex",alignItems:"flex-start",justifyContent:"flex-end"}} onClick={e=>e.target===e.currentTarget&&setShowFollowUpsList(false)}>
          <div style={{background:"#111113",borderLeft:"1px solid #1C1C1F",width:420,height:"100%",display:"flex",flexDirection:"column",boxShadow:"-20px 0 60px #0009"}}>
            <div style={{padding:"20px 24px 16px",borderBottom:"1px solid #1C1C1F",display:"flex",justifyContent:"space-between",alignItems:"center",flexShrink:0}}>
              <div>
                <div style={{fontSize:10,letterSpacing:3,color:"#C9935A",fontFamily:"'DM Mono',monospace",marginBottom:4}}>PIPELINE</div>
                <div style={{fontSize:18,fontWeight:800,color:"#E8E6E1"}}>⏰ Follow-Ups</div>
              </div>
              <button onClick={()=>setShowFollowUpsList(false)} style={{background:"none",border:"none",color:"#6B6B70",cursor:"pointer",fontSize:22}}>✕</button>
            </div>
            <div style={{overflowY:"auto",flex:1,padding:16}}>
              {leads.filter(l=>(l.followUps||[]).some(fu=>!fu.done)).length===0&&(
                <div style={{textAlign:"center",color:"#2A2A2E",fontSize:12,padding:"40px 0",fontFamily:"'DM Mono',monospace"}}>No pending follow-ups</div>
              )}
              {leads
                .filter(l=>(l.followUps||[]).some(fu=>!fu.done))
                .sort((a,b)=>{
                  const aNext=(a.followUps||[]).filter(f=>!f.done).sort((x,y)=>new Date(x.ts)-new Date(y.ts))[0]?.ts||"";
                  const bNext=(b.followUps||[]).filter(f=>!f.done).sort((x,y)=>new Date(x.ts)-new Date(y.ts))[0]?.ts||"";
                  return new Date(aNext)-new Date(bNext);
                })
                .map(lead=>{
                  const pending=(lead.followUps||[]).filter(fu=>!fu.done).sort((a,b)=>new Date(a.ts)-new Date(b.ts));
                  const stage=stageOf(lead.stage);
                  return(
                    <div key={lead.id} style={{background:"#111113",border:"1px solid #1C1C1F",borderRadius:10,padding:14,marginBottom:10}}>
                      <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:8,cursor:"pointer"}}
                        onClick={()=>{setSelected(lead);setShowFollowUpsList(false);}}>
                        <div>
                          <div style={{fontSize:13,fontWeight:700,color:"#E8E6E1"}}>{lead.name}</div>
                          <div style={{fontSize:10,color:"#45454A",marginTop:2}}>{lead.address}</div>
                        </div>
                        <span style={{fontSize:9,fontFamily:"'DM Mono',monospace",color:stage.color,background:stage.color+"18",border:`1px solid ${stage.color}33`,borderRadius:4,padding:"2px 7px"}}>{stage.label}</span>
                      </div>
                      {pending.map((fu,i)=>{
                        const isOverdue=fu.ts&&new Date(fu.ts)<new Date();
                        return(
                          <div key={i} style={{display:"flex",alignItems:"center",gap:8,padding:"7px 10px",background:isOverdue?"#C0614908":"#0F0F11",border:`1px solid ${isOverdue?"#C0614933":"#1C1C1F"}`,borderRadius:7,marginBottom:6}}>
                            <input type="checkbox" onChange={()=>{
                              const updated=lead.followUps.map((f,idx)=>lead.followUps.indexOf(fu)===idx?{...f,done:true}:f);
                              updateLead({...lead,followUps:updated});
                            }} style={{accentColor:"#5A8A60",cursor:"pointer"}}/>
                            <div style={{flex:1}}>
                              <div style={{fontSize:12,color:isOverdue?"#C06149":"#C4C2BD",fontWeight:600}}>{fu.note}</div>
                              <div style={{fontSize:10,color:isOverdue?"#C06149":"#C9935A",fontFamily:"'DM Mono',monospace",marginTop:2}}>{isOverdue?"⚠ OVERDUE · ":""}{fu.due}</div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  );
                })
              }
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
